import { useState, useEffect, useRef } from "react";
import usePersistentPageState from "../hooks/usePersistentPageState";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../config";
import Layout from "./Layout";
import usePageMeta from "../hooks/usePageMeta";
import { useAppPermissions } from "../context/PermissionContext";
import useAuth from "../hooks/useAuth";
import API from "../api";
import ActionDropdown from "../components/ActionDropdown";
import StaffAvatarCircle from "../components/StaffAvatarCircle";
import {
  employeeRowForAvatar,
  fetchAccountAvatarBatch,
} from "../utils/accountAvatarUrl";
import AddAccount_Admin from "../components/forms/AddAccount_Admin";
import ClientDetailModal from "../components/ClientDetailModal";
import AddRole_Admin from "../components/forms/AddRole_admin";
import AclPermissionPopover from "../components/acl/AclPermissionPopover";
import VerifyAccountModal from "../components/VerifyAccountModal";
import CreateOneTimePasswordModal from "../components/CreateOneTimePasswordModal";
import GeneratedPasswordModal from "../components/GeneratedPasswordModal";
import {
  impersonateAccount,
  generateImpersonationLink,
  canImpersonateFromCurrentSession,
} from "../utils/auth";
import { formatAppDate, formatAppDateTime, formatAppDistanceToNow, formatAppTime, parseDbDateTime } from "../utils/appDateTime";
import { formatIdentificationAction } from "../utils/identificationActions";
import {
  format,
  isThisYear,
  differenceInHours,
} from "date-fns";
import {
  AtlasFilterPanel,
  AtlasFilterChipBar,
  useFilterPanel,
  AdminAccountsFilterFields,
  buildAdminAccountsFilterChips,
  ACCOUNTS_SORT_OPTIONS,
  AdminRolesFilterFields,
  buildAdminRolesFilterChips,
  ROLES_SORT_OPTIONS,
  AdminSessionsFilterFields,
  buildAdminSessionsFilterChips,
  SESSIONS_SORT_OPTIONS,
  AdminSigninsFilterFields,
  buildAdminSigninsFilterChips,
  SIGNINS_SORT_OPTIONS,
  AdminIdentificationsFilterFields,
  buildAdminIdentificationsFilterChips,
  IDENTIFICATIONS_SORT_OPTIONS,
  AdminTrustedDevicesFilterFields,
  buildAdminTrustedDevicesFilterChips,
  TRUSTED_SORT_OPTIONS,
  AdminOtpsFilterFields,
  buildAdminOtpsFilterChips,
  OTPS_SORT_OPTIONS,
  AdminInvitesFilterFields,
  buildAdminInvitesFilterChips,
  INVITES_SORT_OPTIONS,
} from "../components/filters";
import { AtlasListToolbar } from "../components/list-toolbar";
import {
  AtlasPageHeader,
  AtlasPageDivider,
  AtlasTabBar,
} from "../components/page-layout";
import { showConfirm, showError, showSuccess } from "../utils/dialog";
import {
  AdminSigninBrowserCell,
  AdminSigninDeviceCell,
  AdminSigninLocationCell,
} from "../components/admin/signinDisplay";
import windowsIcon from "../assets/windows.svg";
import macIcon from "../assets/mac.svg";
import androidIcon from "../assets/android.svg";
import iosIcon from "../assets/ios.svg";
import chromeIcon from "../assets/chrome.svg";
import safariIcon from "../assets/safari.svg";
import firefoxIcon from "../assets/firefox.svg";
import edgeIcon from "../assets/edge.svg";

/** DB may return null for lat/lng — Number(null) is 0 in JS, which wrongly shows 0.0000, 0.0000 on maps. */
function adminLocationCoord(value) {
  if (value == null || value === "") return NaN;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

/** Hide localhost / synthetic keys in the IP audit line. */
function adminDisplayableIp(value) {
  if (value == null || value === "") return false;
  const s = String(value).trim().replace(/^::ffff:/i, "");
  if (!s) return false;
  if (s === "::1" || s === "127.0.0.1" || s.toLowerCase() === "localhost") return false;
  if (s.startsWith("anon-gps-")) return false;
  return true;
}

const normalizeAclRolesResponse = (payload) => {
  const aclRoles = payload?.data?.aclRoles;
  if (Array.isArray(aclRoles)) {
    const rows = aclRoles.map((r) => ({
      role_id: r.role_id ?? r.id,
      id: r.id ?? r.role_id,
      name: r.name || "",
      level: r.level || "",
      permissions: Array.isArray(r.permissions)
        ? r.permissions
            .filter(
              (p) => typeof p === "string" || Number(p?.access) > 0
            )
            .map((p) =>
              typeof p === "string" ? p : p?.name || p?.value || ""
            )
            .filter(Boolean)
        : [],
      raw_permissions: Array.isArray(r.permissions) ? r.permissions : [],
      aclPermissions: Array.isArray(r.aclPermissions) ? r.aclPermissions : [],
      description: r.description || "",
      state: r.state || "active",
      allPermissions: !!r.allPermissions,
    }));
    return {
      rows,
      total: payload?.data?.paging?.records ?? rows.length,
    };
  }

  const fallback = Array.isArray(payload?.data) ? payload.data : [];
  return {
    rows: fallback,
    total: payload?.total ?? fallback.length,
  };
};

function PermissionsCell({ rawPermissions = [] }) {
  const granted = (Array.isArray(rawPermissions) ? rawPermissions : []).filter(
    (p) => Number(p?.access) > 0,
  );

  if (!granted.length) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {granted.map((p, i) => (
        <AclPermissionPopover key={p.value || p.permission || i} permission={p} />
      ))}
    </div>
  );
}

const buildAclPermissionsFromRoleForm = (form) => {
  const accessMap = { none: 0, read: 5, write: 10, full: 15 };
  const toAccess = (v) => accessMap[String(v || "none").toLowerCase()] ?? 0;
  if (form?.level === "client") {
    return [
      {
        permission: "jobsupervisor",
        access: toAccess(form?.permissions?.clientPortal?.timesheetSignoff),
      },
      {
        permission: "signoffmanager",
        access: toAccess(form?.permissions?.clientPortal?.timesheetSignoffManager),
      },
    ];
  }
  const clientsAccess =
    form?.permissions?.clients?.access ??
    (form?.permissions?.clients?.write ? "write" : "none");
  const preferencesAccess =
    form?.permissions?.preferences?.access ??
    (form?.permissions?.preferences?.write ? "write" : "none");
  return [
    { permission: "accounting", access: toAccess(form?.permissions?.accounting?.access) },
    { permission: "admin", access: toAccess(form?.permissions?.admin?.access) },
    { permission: "system", access: toAccess(form?.permissions?.system?.access) },
    { permission: "client", access: toAccess(clientsAccess) },
    { permission: "preferences", access: toAccess(preferencesAccess) },
    { permission: "officememployee", access: toAccess(form?.permissions?.personnel?.officeStaff) },
    { permission: "employeecandidate", access: toAccess(form?.permissions?.personnel?.tempStaff) },
    { permission: "shiftactual", access: toAccess(form?.permissions?.jobs?.shiftActuals) },
    { permission: "shift", access: toAccess(form?.permissions?.jobs?.shifts) },
  ];
};

const emptyRoleForm = {
  name: "",
  description: "",
  level: "app",
  app: "",
  client: "",
  enabled: false,
  permissions: {
    accounting: { access: "none" },
    admin: { access: "none" },
    system: { access: "none" },
    clients: { access: "none", write: false },
    preferences: { access: "none", write: false },
    personnel: {
      officeStaff: "none",
      tempStaff: "none",
    },
    jobs: {
      shiftActuals: "none",
      shifts: "none",
    },
    clientPortal: {
      timesheetSignoff: "none",
      timesheetSignoffManager: "none",
    },
  },
};

const accessTokenFromValue = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "none";
  if (num >= 15) return "full";
  if (num >= 10) return "write";
  if (num >= 5) return "read";
  return "none";
};

const roleFormFromAclRole = (role = {}) => {
  const next = JSON.parse(JSON.stringify(emptyRoleForm));
  const aclPermissions = Array.isArray(role.aclPermissions)
    ? role.aclPermissions
    : [];
  const permMap = aclPermissions.reduce((acc, p) => {
    if (p?.permission) acc[p.permission] = Number(p.access) || 0;
    return acc;
  }, {});

  next.name = role.name || "";
  next.description = role.description || "";
  next.level = role.level || "app";
  next.enabled = !!role.allPermissions;
  next.permissions.accounting.access = accessTokenFromValue(permMap.accounting);
  next.permissions.admin.access = accessTokenFromValue(permMap.admin);
  next.permissions.system.access = accessTokenFromValue(permMap.system);
  next.permissions.clients.access = accessTokenFromValue(permMap.client);
  next.permissions.clients.write = (permMap.client || 0) >= 10;
  next.permissions.preferences.access = accessTokenFromValue(permMap.preferences);
  next.permissions.preferences.write = (permMap.preferences || 0) >= 10;
  next.permissions.personnel.officeStaff = accessTokenFromValue(
    permMap.officememployee
  );
  next.permissions.personnel.tempStaff = accessTokenFromValue(
    permMap.employeecandidate
  );
  next.permissions.jobs.shiftActuals = accessTokenFromValue(permMap.shiftactual);
  next.permissions.jobs.shifts = accessTokenFromValue(permMap.shift);
  next.permissions.clientPortal.timesheetSignoff =
    (permMap.jobsupervisor || 0) >= 10 ? "write" : "none";
  next.permissions.clientPortal.timesheetSignoffManager =
    (permMap.signoffmanager || 0) >= 10 ? "write" : "none";

  return next;
};

function formatLastSignin(signinDate) {
  if (!signinDate) return "Never";

  const date = parseDbDateTime(signinDate);
  if (!date) return "Never";

  const hoursAgo = differenceInHours(new Date(), date);

  if (hoursAgo < 24) {
    return formatAppDistanceToNow(signinDate)
      .replace("about ", "")
      .replace("less than a minute ago", "just now");
  }

  return formatAppDateTime(signinDate);
}

const Accounts = () => {
  usePageMeta("Accounts");
  const { canWriteAdmin, isSuperAdmin } = useAppPermissions();
  const canImpersonate = canImpersonateFromCurrentSession(isSuperAdmin);
  const adminReadOnly = !canWriteAdmin;
  const filterPanel = useFilterPanel();
  const invitesFilterPanel = useFilterPanel();

  // Data state
  const [accounts, setAccounts] = useState([]);
  const [accountsTotal, setAccountsTotal] = useState(0);
  const [accountAvatarUrls, setAccountAvatarUrls] = useState({});
  const [roles, setRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availableRoleLevels, setAvailableRoleLevels] = useState([]);
  const [rolesTotal, setRolesTotal] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);
  const [signins, setSignins] = useState([]);
  const [signinsTotal, setSigninsTotal] = useState(0);
  const [identifications, setIdentifications] = useState([]);
  const [identificationsTotal, setIdentificationsTotal] = useState(0);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [previewImageCaption, setPreviewImageCaption] = useState("Preview");
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [trustedDevicesTotal, setTrustedDevicesTotal] = useState(0);
  const [otps, setOtps] = useState([]);
  const [otpsTotal, setOtpsTotal] = useState(0);
  const [selectedTrustedDeviceIds, setSelectedTrustedDeviceIds] = useState([]);

  const [selectedTab, setSelectedTab] = useState("ACCOUNTS");
  const [searchTerm, setSearchTerm] = usePersistentPageState(
    "page_search_admin_accounts",
    ""
  );
  const [openModal, setOpenModal] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const [accountsPageSize, setAccountsPageSize] = useState(25);
  const [otherTabsPageSize, setOtherTabsPageSize] = useState(25);
  // Accounts sorting state
  const [sortKey, setSortKey] = useState("created");
  const [sortDir, setSortDir] = useState("desc");
  // Roles sorting state
  const [rolesSortKey, setRolesSortKey] = useState("name");
  const [rolesSortDir, setRolesSortDir] = useState("asc");
  // Sessions sorting state
  const [sessionsSortKey, setSessionsSortKey] = useState("create_date");
  const [sessionsSortDir, setSessionsSortDir] = useState("desc");
  // API Keys sorting state
  const [apiKeysSortKey, setApiKeysSortKey] = useState("expiry_date");
  const [apiKeysSortDir, setApiKeysSortDir] = useState("desc");
  // Signins sorting state
  const [signinsSortKey, setSigninsSortKey] = useState("signin_date");
  const [signinsSortDir, setSigninsSortDir] = useState("desc");
  // Identifications sorting state
  const [identSortKey, setIdentSortKey] = useState("create_date");
  const [identSortDir, setIdentSortDir] = useState("desc");
  // Trusted devices sorting state
  const [trustedSortKey, setTrustedSortKey] = useState("signin_date");
  const [trustedSortDir, setTrustedSortDir] = useState("desc");

  const handleTrustedColumnSort = (colKey) => {
    setCurrentPage(1);
    if (trustedSortKey === colKey) {
      setTrustedSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setTrustedSortKey(colKey);
    setTrustedSortDir(
      colKey === "create_date" || colKey === "signin_date" ? "desc" : "asc",
    );
  };

  const trustedSortIndicator = (colKey) =>
    trustedSortKey === colKey ? (
      <span className="text-sm text-white/90">
        {trustedSortDir === "asc" ? "▲" : "▼"}
      </span>
    ) : null;
  // OTPs sorting state
  const [otpsSortKey, setOtpsSortKey] = useState("create_date");
  const [otpsSortDir, setOtpsSortDir] = useState("desc");
  // filterBy is now part of the persisted adminAccountsFilters bundle (see above)
  const [showInvites, setShowInvites] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [showVerifyOtpCreate, setShowVerifyOtpCreate] = useState(false);
  const [showCreateOtpModal, setShowCreateOtpModal] = useState(false);
  const [generatedOneTimePassword, setGeneratedOneTimePassword] = useState("");
  const [impersonateDialog, setImpersonateDialog] = useState({
    open: false,
    accountId: null,
  });
  const [impersonateMode, setImpersonateMode] = useState("new");
  const [impersonateBusy, setImpersonateBusy] = useState(false);
  const [impersonationCopyUrl, setImpersonationCopyUrl] = useState("");
  const [impersonationCopied, setImpersonationCopied] = useState(false);

  // UI filter panel state & refs
  const [openFilterPanel, setOpenFilterPanel] = useState(null);
  const statusDropdownRef = useRef(null);
  const signinStatusDropdownRef = useRef(null);
  const summarySortDropdownRef = useRef(null);
  const accountSortDropdownRef = useRef(null);
  const accountPageSizeDropdownRef = useRef(null);
  const accountRoleRef = useRef(null);
  const accountTypeRef = useRef(null);
  const accountTypeDropdownRef = useRef(null);
  const signinFromRef = useRef(null);
  const signinToRef = useRef(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showSigninStatusDropdown, setShowSigninStatusDropdown] = useState(false);
  const [showSummarySortDropdown, setShowSummarySortDropdown] = useState(false);
  const [showAccountSortDropdown, setShowAccountSortDropdown] = useState(false);
  const [showAccountPageSizeDropdown, setShowAccountPageSizeDropdown] = useState(false);
  // Add state for Type filter dropdown
  const [showAccountTypeDropdown, setShowAccountTypeDropdown] = useState(false);
  const [accountFilterFocusTarget, setAccountFilterFocusTarget] = useState("");
  const [signinFilterFocusTarget, setSigninFilterFocusTarget] = useState("");

  // Account and all other tab filter states (persisted across navigation)
  const [adminAccountsFilters, setAdminAccountsFilters] = usePersistentPageState(
    "page_filters_admin_accounts",
    {
      filterBy: "all",
      rolesFilterBy: "all",
      typeFilterBy: "all",
      selectedStatuses: [],
      divisionFilter: "",
      accountSortKey: "name",
      accountSortDir: "asc",
      showDeleted: false,
      fromDate: "",
      toDate: "",
      selectedSigninStatuses: [],
      deviceTypeFilter: "all",
      trustedDeviceType: "all",
      trustedFromDate: "",
      trustedToDate: "",
      trustedShowDeleted: false,
      otpsFromDate: "",
      otpsToDate: "",
      levelFilterBy: "all",
    }
  );
  const filterBy = adminAccountsFilters.filterBy ?? "all";
  const setFilterBy = (val) => setAdminAccountsFilters((prev) => ({ ...prev, filterBy: typeof val === "function" ? val(prev.filterBy) : val }));
  const rolesFilterBy = adminAccountsFilters.rolesFilterBy ?? "all";
  const setRolesFilterBy = (val) => setAdminAccountsFilters((prev) => ({ ...prev, rolesFilterBy: typeof val === "function" ? val(prev.rolesFilterBy) : val }));
  const typeFilterBy = adminAccountsFilters.typeFilterBy ?? "all";
  const setTypeFilterBy = (val) => setAdminAccountsFilters((prev) => ({ ...prev, typeFilterBy: typeof val === "function" ? val(prev.typeFilterBy) : val }));
  const selectedStatuses = adminAccountsFilters.selectedStatuses ?? [];
  const setSelectedStatuses = (val) => setAdminAccountsFilters((prev) => ({ ...prev, selectedStatuses: typeof val === "function" ? val(prev.selectedStatuses) : val }));
  const divisionFilter = adminAccountsFilters.divisionFilter ?? "";
  const setDivisionFilter = (val) => setAdminAccountsFilters((prev) => ({ ...prev, divisionFilter: typeof val === "function" ? val(prev.divisionFilter) : val }));
  const accountSortKey = adminAccountsFilters.accountSortKey ?? "name";
  const setAccountSortKey = (val) => setAdminAccountsFilters((prev) => ({ ...prev, accountSortKey: typeof val === "function" ? val(prev.accountSortKey) : val }));
  const accountSortDir = adminAccountsFilters.accountSortDir ?? "asc";
  const setAccountSortDir = (val) => setAdminAccountsFilters((prev) => ({ ...prev, accountSortDir: typeof val === "function" ? val(prev.accountSortDir) : val }));
  const showDeleted = adminAccountsFilters.showDeleted ?? false;
  const setShowDeleted = (val) => setAdminAccountsFilters((prev) => ({ ...prev, showDeleted: typeof val === "function" ? val(prev.showDeleted) : val }));
  // Computed helpers derived from typeFilterBy
  const hasTypeFilter = Array.isArray(typeFilterBy)
    ? typeFilterBy.length > 0
    : typeFilterBy !== "all" && String(typeFilterBy || "").trim() !== "";
  const typeChipLabel = Array.isArray(typeFilterBy)
    ? typeFilterBy.length > 2
      ? `${typeFilterBy[0]},${typeFilterBy[1]}...`
      : typeFilterBy.join(",")
    : String(typeFilterBy || "");

  // Signins / date filters
  const fromDate = adminAccountsFilters.fromDate ?? "";
  const setFromDate = (val) => setAdminAccountsFilters((prev) => ({ ...prev, fromDate: typeof val === "function" ? val(prev.fromDate) : val }));
  const toDate = adminAccountsFilters.toDate ?? "";
  const setToDate = (val) => setAdminAccountsFilters((prev) => ({ ...prev, toDate: typeof val === "function" ? val(prev.toDate) : val }));
  const selectedSigninStatuses = adminAccountsFilters.selectedSigninStatuses ?? [];
  const setSelectedSigninStatuses = (val) => setAdminAccountsFilters((prev) => ({ ...prev, selectedSigninStatuses: typeof val === "function" ? val(prev.selectedSigninStatuses) : val }));
  const signinStatusChipLabel =
    selectedSigninStatuses.length > 2
      ? `${selectedSigninStatuses[0]},${selectedSigninStatuses[1]}...`
      : selectedSigninStatuses.join(",");

  // Identification / trusted devices filters
  const deviceTypeFilter = adminAccountsFilters.deviceTypeFilter ?? "all";
  const setDeviceTypeFilter = (val) => setAdminAccountsFilters((prev) => ({ ...prev, deviceTypeFilter: typeof val === "function" ? val(prev.deviceTypeFilter) : val }));
  const trustedDeviceType = adminAccountsFilters.trustedDeviceType ?? "all";
  const setTrustedDeviceType = (val) => setAdminAccountsFilters((prev) => ({ ...prev, trustedDeviceType: typeof val === "function" ? val(prev.trustedDeviceType) : val }));
  const trustedFromDate = adminAccountsFilters.trustedFromDate ?? "";
  const setTrustedFromDate = (val) => setAdminAccountsFilters((prev) => ({ ...prev, trustedFromDate: typeof val === "function" ? val(prev.trustedFromDate) : val }));
  const trustedToDate = adminAccountsFilters.trustedToDate ?? "";
  const setTrustedToDate = (val) => setAdminAccountsFilters((prev) => ({ ...prev, trustedToDate: typeof val === "function" ? val(prev.trustedToDate) : val }));
  const trustedShowDeleted = adminAccountsFilters.trustedShowDeleted ?? false;
  const setTrustedShowDeleted = (val) => setAdminAccountsFilters((prev) => ({ ...prev, trustedShowDeleted: typeof val === "function" ? val(prev.trustedShowDeleted) : val }));

  // OTP filters
  const otpsFromDate = adminAccountsFilters.otpsFromDate ?? "";
  const setOtpsFromDate = (val) => setAdminAccountsFilters((prev) => ({ ...prev, otpsFromDate: typeof val === "function" ? val(prev.otpsFromDate) : val }));
  const otpsToDate = adminAccountsFilters.otpsToDate ?? "";
  const setOtpsToDate = (val) => setAdminAccountsFilters((prev) => ({ ...prev, otpsToDate: typeof val === "function" ? val(prev.otpsToDate) : val }));

  // Roles panel filter
  const levelFilterBy = adminAccountsFilters.levelFilterBy ?? "all";
  const setLevelFilterBy = (val) => setAdminAccountsFilters((prev) => ({ ...prev, levelFilterBy: typeof val === "function" ? val(prev.levelFilterBy) : val }));

  // Handlers for toggling multi-select status lists
  const handleStatusChange = (status) =>
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );

  const handleSigninStatusChange = (status) =>
    setSelectedSigninStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );

  const normalizeSigninState = (state) =>
    String(state || "")
      .toLowerCase()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\s+/g, "");

  const getSigninStateLabel = (state) => {
    const token = normalizeSigninState(state);
    const map = {
      success: "Success",
      failure: "Failure",
      failed: "Failed",
      reset: "Reset",
      verificationrequested: "Verification Requested",
      verificationfailed: "Verification Failed",
      passwordreset: "Password Reset",
    };
    return map[token] || (state ? String(state) : "");
  };

  const getSigninStateColor = (state) => {
    const token = normalizeSigninState(state);
    if (token === "success") return "text-green-600";
    if (token === "failure" || token === "failed" || token === "verificationfailed") return "text-red-600";
    if (token === "verificationrequested" || token === "reset" || token === "passwordreset") return "text-amber-600";
    return "text-gray-700";
  };

  const openSigninFilterWithTarget = (target) => {
    setOpenFilterPanel("SIGN INS");
    setSigninFilterFocusTarget(target);
  };

  const openAccountsFilterWithTarget = (target) => {
    setOpenFilterPanel("ACCOUNTS");
    setAccountFilterFocusTarget(target);
  };

  const getAccountsSortLabel = (key) => {
    if (key === "name") return "Name";
    if (key === "type") return "Type";
    if (key === "last_sign_in" || key === "signin") return "Last";
    return "Created";
  };

  const getIdentificationSortLabel = (key) => {
    if (key === "name") return "Account";
    if (key === "client_name") return "Client";
    return "Date";
  };

  useEffect(() => {
    setSelectedTrustedDeviceIds((prev) =>
      prev.filter((id) =>
        trustedDevices.some((device) => device.trusted_device_id === id)
      )
    );
  }, [trustedDevices]);

  const toggleTrustedDevice = (id) => {
    setSelectedTrustedDeviceIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleAllTrustedDevices = (checked) => {
    if (checked) {
      setSelectedTrustedDeviceIds(
        sortedTrustedDevices.map((device) => device.trusted_device_id)
      );
    } else {
      setSelectedTrustedDeviceIds([]);
    }
  };

  const bulkSignOutTrustedDevices = async () => {
    if (selectedTrustedDeviceIds.length === 0) return;
    if (
      !window.confirm(
        `Are you sure you want to sign out ${selectedTrustedDeviceIds.length} selected device(s)?`
      )
    )
      return;
    setIsLoading(true);
    let successCount = 0;
    const failures = [];
    for (const id of selectedTrustedDeviceIds) {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/trusted-devices/${id}`, {
          method: "DELETE",
          credentials: "include",
        });
        const data = await res.json();
        if (!res.ok)
          throw new Error(data.message || "Failed to sign out device");
        successCount += 1;
      } catch (err) {
        failures.push({ id, message: err.message || "Failed to sign out" });
      }
    }
    if (successCount > 0) {
      showSuccess(`Signed out ${successCount} device(s)`);
    }
    if (failures.length > 0) {
      showError(
        `Failed to sign out ${failures.length} device(s). First error: ${failures[0].message}`
      );
    }
    setSelectedTrustedDeviceIds([]);
    setRefreshTick((t) => t + 1);
    setIsLoading(false);
  };

  const bulkDeleteTrustedDevices = async () => {
    if (selectedTrustedDeviceIds.length === 0) return;
    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedTrustedDeviceIds.length} selected device(s)? This action cannot be undone.`
      )
    )
      return;
    setIsLoading(true);
    let successCount = 0;
    const failures = [];
    for (const id of selectedTrustedDeviceIds) {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/trusted-devices/${id}`, {
          method: "DELETE",
          credentials: "include",
        });
        const data = await res.json();
        if (!res.ok)
          throw new Error(data.message || "Failed to delete device");
        successCount += 1;
      } catch (err) {
        failures.push({ id, message: err.message || "Failed to delete" });
      }
    }
    if (successCount > 0) {
      showSuccess(`Deleted ${successCount} device(s)`);
    }
    if (failures.length > 0) {
      showError(
        `Failed to delete ${failures.length} device(s). First error: ${failures[0].message}`
      );
    }
    setSelectedTrustedDeviceIds([]);
    setRefreshTick((t) => t + 1);
    setIsLoading(false);
  };

  const handleRemoveChip = (chip) => {
    const key = chip.key;
    if (selectedTab === "ACCOUNTS") {
      if (key === "rolesFilterBy") setRolesFilterBy("all");
      if (key === "typeFilterBy") setTypeFilterBy("all");
      if (key === "selectedStatuses") setSelectedStatuses([]);
      if (key === "showDeleted") setShowDeleted(false);
    } else if (selectedTab === "ROLES") {
      if (key === "levelFilterBy") setLevelFilterBy("all");
      if (key === "showDeleted") setShowDeleted(false);
    } else if (selectedTab === "SIGN INS") {
      if (key === "selectedSigninStatuses") setSelectedSigninStatuses([]);
      if (key === "fromDate") setFromDate("");
      if (key === "toDate") setToDate("");
    } else if (selectedTab === "IDENTIFICATIONS") {
      if (key === "deviceTypeFilter") setDeviceTypeFilter("all");
    } else if (selectedTab === "CLIENT DEVICES") {
      if (key === "trustedDeviceType") setTrustedDeviceType("all");
      if (key === "trustedFromDate") setTrustedFromDate("");
      if (key === "trustedToDate") setTrustedToDate("");
      if (key === "trustedShowDeleted") setTrustedShowDeleted(false);
    } else if (selectedTab === "ONE TIME PASSWORDS") {
      if (key === "otpsFromDate") setOtpsFromDate("");
      if (key === "otpsToDate") setOtpsToDate("");
    }
    setCurrentPage(1);
  };

  const handleClearAllChips = () => {
    if (selectedTab === "ACCOUNTS") {
      setRolesFilterBy("all");
      setTypeFilterBy("all");
      setSelectedStatuses([]);
      setDivisionFilter("");
      setShowDeleted(false);
    } else if (selectedTab === "ROLES") {
      setLevelFilterBy("all");
      setShowDeleted(false);
    } else if (selectedTab === "SIGN INS") {
      setFromDate("");
      setToDate("");
      setSelectedSigninStatuses([]);
      setSigninFilterFocusTarget("");
    } else if (selectedTab === "IDENTIFICATIONS") {
      setDeviceTypeFilter("all");
    } else if (selectedTab === "CLIENT DEVICES") {
      setTrustedDeviceType("all");
      setTrustedFromDate("");
      setTrustedToDate("");
      setTrustedShowDeleted(false);
    } else if (selectedTab === "ONE TIME PASSWORDS") {
      setOtpsFromDate("");
      setOtpsToDate("");
    }
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortArrow = ({ colKey, activeKey, activeDir, onChange }) => (
    <button
      onClick={() => {
        const k = activeKey ?? sortKey;
        const d = activeDir ?? sortDir;
        if ((activeKey ?? sortKey) === colKey) {
          const nextDir = d === "asc" ? "desc" : "asc";
          if (onChange) onChange(colKey, nextDir);
          else setSortDir(nextDir);
        } else {
          if (onChange) onChange(colKey, "asc");
          else {
            setSortKey(colKey);
            setSortDir("asc");
          }
        }
      }}
      className="float-right mr-1 text-sm text-white/90 hover:text-white"
      title="Sort"
    >
      {(activeKey ?? sortKey) === colKey ? ((activeDir ?? sortDir) === 'asc' ? '▲' : '▼') : null}
    </button>
  );

  const getCurrentSortMeta = () => {
    let key = "created";
    let dir = "desc";
    let map = {};
    switch (selectedTab) {
      case "ACCOUNTS":
        key = sortKey;
        dir = sortDir;
        map = {
          name: "Name",
          type: "Type",
          signin: "Last Sign In",
          last_sign_in: "Last Sign In",
          created: "Created",
        };
        break;
      case "ROLES":
        key = rolesSortKey;
        dir = rolesSortDir;
        map = { name: "Name", level: "Level" };
        break;
      case "SESSIONS":
        key = sessionsSortKey;
        dir = sessionsSortDir;
        map = {
          name: "Account",
          state: "Status",
          create_date: "Created",
          expiry_date: "Expires",
        };
        break;
      // case 'API KEYS':
      //   key = apiKeysSortKey; dir = apiKeysSortDir; map = { account_id: 'Account', state: 'Status', expiry_date: 'Expiry' }; break;
      case "SIGN INS":
        key = signinsSortKey;
        dir = signinsSortDir;
        map = { name: "Account", state: "Status", signin_date: "Date", create_date: "Date" };
        break;
      case "IDENTIFICATIONS":
        key = identSortKey;
        dir = identSortDir;
        map = { name: "Account", client_name: 'Client', create_date: "Date" };
        break;
      case "CLIENT DEVICES":
        key = trustedSortKey;
        dir = trustedSortDir;
        map = { client_name: "Client", signin_date: "Last Sign in By", create_date: "Created" };
        break;
      case "ONE TIME PASSWORDS":
        key = otpsSortKey;
        dir = otpsSortDir;
        map = {
          state: "Status",
          create_date: "Created",
          used_date: "Used Date",
        };
        break;
      default:
        break;
    }
    const keyLabel = map[key] || key;
    const dirLabel = dir === "asc" ? "Ascending" : "Descending";
    return { keyLabel, dirLabel };
  };

  const getSummarySortOptions = () => {
    switch (selectedTab) {
      case "ROLES":
        return [
          { key: "name", label: "Name" },
          { key: "level", label: "Level" },
        ];
      case "SESSIONS":
        return [
          { key: "create_date", label: "Created" },
          { key: "expiry_date", label: "Expires" },
        ];
      case "SIGN INS":
        return [
          { key: "name", label: "Account" },
          { key: "state", label: "Status" },
          { key: "signin_date", label: "Date" },
        ];
      case "CLIENT DEVICES":
        return [
          { key: "client_name", label: "Client" },
          { key: "signin_date", label: "Last Sign in By" },
          { key: "create_date", label: "Created" },
        ];
      case "ONE TIME PASSWORDS":
        return [
          { key: "state", label: "Status" },
          { key: "create_date", label: "Created" },
          { key: "used_date", label: "Used Date" },
        ];
      default:
        return [];
    }
  };

  const setSummarySortKey = (key) => {
    if (selectedTab === "ROLES") setRolesSortKey(key);
    else if (selectedTab === "SESSIONS") setSessionsSortKey(key);
    else if (selectedTab === "SIGN INS") setSigninsSortKey(key);
    else if (selectedTab === "CLIENT DEVICES") setTrustedSortKey(key);
    else if (selectedTab === "ONE TIME PASSWORDS") setOtpsSortKey(key);
  };

  const toggleSummarySortDirection = () => {
    if (selectedTab === "ROLES") setRolesSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    else if (selectedTab === "SESSIONS") setSessionsSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    else if (selectedTab === "SIGN INS") setSigninsSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    else if (selectedTab === "CLIENT DEVICES") setTrustedSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    else if (selectedTab === "ONE TIME PASSWORDS") setOtpsSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  // Fetch helpers
  const fetchJSON = async (path) => {
    const res = await fetch(`${BASE_URL}${path}`, { credentials: "include" });
    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
    return res.json();
  };

  const avatarSrcForAccount = (accountId) => {
    const key = String(accountId ?? "");
    if (!key || !Object.prototype.hasOwnProperty.call(accountAvatarUrls, key)) {
      return undefined;
    }
    return accountAvatarUrls[key];
  };

  const loadAvatarsForAccountRows = async (rows) => {
    const urls = await fetchAccountAvatarBatch(
      (rows || []).map((row) => row.account_id),
    );
    return urls;
  };

  // Load available roles for ACCOUNTS filter when panel opens (lazy)
  useEffect(() => {
    if (!filterPanel.isOpen || selectedTab !== "ACCOUNTS") return;
    if (availableRoles.length > 0) return;

    let ignore = false;
    const loadRoles = async () => {
      try {
        const data = await fetchJSON(
          `/api/acl/roles?offset=0&limit=500&order=hierarchy,asc&permissions=false&environmentId=null`,
        );
        if (ignore) return;
        const { rows } = normalizeAclRolesResponse(data);
        const names = rows.map((r) => r.name).filter(Boolean);
        setAvailableRoles(names);
      } catch (e) {
        console.warn("Failed to load available roles for filter", e);
      }
    };
    loadRoles();
    return () => {
      ignore = true;
    };
  }, [filterPanel.isOpen, selectedTab, availableRoles.length]);

  // Load role levels for ROLES filter when panel opens (lazy)
  useEffect(() => {
    if (!filterPanel.isOpen || selectedTab !== "ROLES") return;
    if (availableRoleLevels.length > 0) return;

    let ignore = false;
    const loadRoleLevels = async () => {
      try {
        const data = await fetchJSON(
          `/api/acl/roles?offset=0&limit=500&order=hierarchy,asc&permissions=false&environmentId=null`,
        );
        if (ignore) return;
        const { rows } = normalizeAclRolesResponse(data);
        const levels = Array.from(
          new Set(rows.map((r) => (r.level || "").toString()).filter(Boolean)),
        );
        setAvailableRoleLevels(levels);
      } catch (e) {
        console.warn("Failed to load role levels for filter", e);
      }
    };
    loadRoleLevels();
    return () => {
      ignore = true;
    };
  }, [filterPanel.isOpen, selectedTab, availableRoleLevels.length]);

  // Effects: load per tab
  useEffect(() => {
    let ignore = false;
    const load = async () => {
      try {
        setIsLoading(true);
        setError("");
        if (selectedTab === "ACCOUNTS") {
          const params = new URLSearchParams();

          params.set("page", currentPage);
          params.set("limit", accountsPageSize);
          // URLSearchParams already percent-encodes values in .toString() -- pre-encoding
          // searchTerm with encodeURIComponent (as every other tab's fetch does, safe there
          // since those build the URL via plain template-string interpolation instead) double-
          // encoded it here, e.g. "test ceylonx" -> "test%20ceylonx" -> "test%2520ceylonx" on
          // the wire. The backend decodes only once, so it received the literal string
          // "test%20ceylonx" and could never match anything -- this broke every multi-word
          // search from this tab, not just names with data-entry whitespace issues.
          params.set("search", searchTerm || "");
          params.set("sort", sortKey);
          params.set("dir", sortDir);

          if (rolesFilterBy !== "all") {
            params.set("role", rolesFilterBy);
          }

          if (hasTypeFilter) {
            if (Array.isArray(typeFilterBy)) params.set("type", typeFilterBy.join(","));
            else params.set("type", typeFilterBy);
          }

          if (selectedStatuses.length > 0) {
            params.set("status", selectedStatuses.join(","));
          }

          params.set("showDeleted", showDeleted);

          setAccountAvatarUrls({});
          const data = await fetchJSON(
            `/api/admin/accounts?${params.toString()}`
          );

          if (ignore) return;
          const accountRows = data.data || [];
          setAccounts(accountRows);
          setAccountsTotal(data.total || 0);

          try {
            const urls = await loadAvatarsForAccountRows(accountRows);
            if (!ignore) setAccountAvatarUrls(urls);
          } catch {
            if (!ignore) setAccountAvatarUrls({});
          }
        } else if (selectedTab === "ROLES") {
          const q = encodeURIComponent(searchTerm || "");
          const offset = (currentPage - 1) * otherTabsPageSize;
          let url = `/api/acl/roles?offset=${offset}&limit=${otherTabsPageSize}&order=${encodeURIComponent(
            `${rolesSortKey},${rolesSortDir}`
          )}&permissions=true&environmentId=null&search=${q}`;
          if (levelFilterBy && levelFilterBy !== "all")
            url += `&level=${encodeURIComponent(levelFilterBy)}`;
          if (showDeleted) url += `&showDeleted=1`;
          const data = await fetchJSON(url);
          if (ignore) return;
          const normalized = normalizeAclRolesResponse(data);
          setRoles(normalized.rows || []);
          setRolesTotal(normalized.total || 0);
        } else if (selectedTab === "SESSIONS") {
          const q = encodeURIComponent(searchTerm || "");
          setAccountAvatarUrls({});
          const data = await fetchJSON(
            `/api/admin/sessions?page=${currentPage}&limit=${otherTabsPageSize}&search=${q}&sort=${encodeURIComponent(
              sessionsSortKey
            )}&dir=${encodeURIComponent(sessionsSortDir)}`
          );
          if (ignore) return;
          const sessionRows = data.data || [];
          setSessions(sessionRows);
          setSessionsTotal(data.total || 0);
          try {
            const urls = await loadAvatarsForAccountRows(sessionRows);
            if (!ignore) setAccountAvatarUrls(urls);
          } catch {
            if (!ignore) setAccountAvatarUrls({});
          }
          // } else if (selectedTab === "API KEYS") {
          //   const data = await fetchJSON(`/api/admin/api-keys`);
          //   if (ignore) return;
          //   setApiKeys(data.data || []);
        } else if (selectedTab === "SIGN INS") {
          const q = encodeURIComponent(searchTerm || "");
          setAccountAvatarUrls({});
          const data = await fetchJSON(
            `/api/admin/signins?page=${currentPage}
&limit=${otherTabsPageSize}
&search=${q}
&sort=${signinsSortKey}
&dir=${signinsSortDir}
&from=${fromDate}
&to=${toDate}
&status=${selectedSigninStatuses.join(",")}`
          );
          if (ignore) return;
          const signinRows = data.data || [];
          setSignins(signinRows);
          setSigninsTotal(data.total || 0);
          try {
            const urls = await loadAvatarsForAccountRows(signinRows);
            if (!ignore) setAccountAvatarUrls(urls);
          } catch {
            if (!ignore) setAccountAvatarUrls({});
          }
        } else if (selectedTab === "IDENTIFICATIONS") {
          const q = encodeURIComponent(searchTerm || "");
          setAccountAvatarUrls({});
          const data = await fetchJSON(
            `/api/admin/identifications?page=${currentPage}
&limit=${otherTabsPageSize}
&search=${q}
&sort=${identSortKey}
&dir=${identSortDir}
&type=${deviceTypeFilter}`
          );
          if (ignore) return;
          const identificationRows = data.data || [];
          setIdentifications(identificationRows);
          setIdentificationsTotal(data.total || 0);
          try {
            const urls = await loadAvatarsForAccountRows(identificationRows);
            if (!ignore) setAccountAvatarUrls(urls);
          } catch {
            if (!ignore) setAccountAvatarUrls({});
          }
        } else if (selectedTab === "CLIENT DEVICES") {
          const q = encodeURIComponent(searchTerm || "");
          const data = await fetchJSON(
            `/api/admin/trusted-devices?page=${currentPage}&limit=${otherTabsPageSize}&search=${q}&sort=${trustedSortKey}&dir=${trustedSortDir}&type=${trustedDeviceType}&from=${trustedFromDate}&to=${trustedToDate}&showDeleted=${trustedShowDeleted}`
          );
          if (ignore) return;
          const rows = data.data || [];
          setTrustedDevices(rows);
          setTrustedDevicesTotal(data.total || 0);
          try {
            const urls = await loadAvatarsForAccountRows(rows);
            if (!ignore) setAccountAvatarUrls(urls);
          } catch {
            if (!ignore) setAccountAvatarUrls({});
          }
        } else if (selectedTab === "ONE TIME PASSWORDS") {
          const q = encodeURIComponent(searchTerm || "");
          const data = await fetchJSON(
            `/api/admin/otps?page=${currentPage}
&limit=${otherTabsPageSize}
&search=${q}
&sort=${otpsSortKey}
&dir=${otpsSortDir}
&from=${otpsFromDate}
&to=${otpsToDate}
`
          );
          if (ignore) return;
          setOtps(data.data || []);
          setOtpsTotal(data.total || 0);
        }
      } catch (e) {
        if (!ignore) setError(e.message || "Failed to load");
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, [
    selectedTab,
    currentPage,
    searchTerm,
    refreshTick,
    accountsPageSize,
    otherTabsPageSize,

    // ✅ ACCOUNTS FILTERS
    sortKey,
    sortDir,
    rolesFilterBy,
    typeFilterBy,
    selectedStatuses,
    showDeleted,
    divisionFilter,

    // ✅ ROLES FILTERS
    levelFilterBy,
    rolesSortKey,
    rolesSortDir,

    // ✅ SESSIONS
    sessionsSortKey,
    sessionsSortDir,

    // ✅ SIGN INS FILTERS
    signinsSortKey,
    signinsSortDir,
    fromDate,
    toDate,
    selectedSigninStatuses,

    // ✅ IDENTIFICATIONS FILTERS
    identSortKey,
    identSortDir,
    deviceTypeFilter,

    // ✅ CLIENT DEVICES FILTERS
    trustedSortKey,
    trustedSortDir,
    trustedDeviceType,
    trustedFromDate,
    trustedToDate,
    trustedShowDeleted,

    // ✅ OTPs FILTERS
    otpsSortKey,
    otpsSortDir,
    otpsFromDate,
    otpsToDate,
  ]);

  // Reset pagination on search/tab change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedTab,
    searchTerm,

    // ACCOUNTS FILTERS
    sortKey,
    sortDir,
    rolesFilterBy,
    typeFilterBy,
    selectedStatuses,
    showDeleted,
    divisionFilter,

    // ROLES FILTERS
    levelFilterBy,
    rolesSortKey,
    rolesSortDir,

    // SESSIONS
    sessionsSortKey,
    sessionsSortDir,

    // SIGN INS FILTERS
    signinsSortKey,
    signinsSortDir,
    fromDate,
    toDate,
    selectedSigninStatuses,

    // IDENTIFICATIONS FILTERS
    identSortKey,
    identSortDir,
    deviceTypeFilter,

    // CLIENT DEVICES FILTERS
    trustedSortKey,
    trustedSortDir,
    trustedDeviceType,
    trustedFromDate,
    trustedToDate,
    trustedShowDeleted,

    // OTPs FILTERS
    otpsSortKey,
    otpsSortDir,
    otpsFromDate,
    otpsToDate,
  ]);

  // Reset sort to default when switching back to ACCOUNTS, to show default
  useEffect(() => {
    if (selectedTab === "ACCOUNTS") {
      setSortKey("created");
      setSortDir("desc");
    }
    if (selectedTab === "ONE TIME PASSWORDS" && otpsSortKey === "email") {
      setOtpsSortKey("create_date");
    }
  }, [selectedTab]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
      if (
        signinStatusDropdownRef.current &&
        !signinStatusDropdownRef.current.contains(event.target)
      ) {
        setShowSigninStatusDropdown(false);
      }
      if (
        summarySortDropdownRef.current &&
        !summarySortDropdownRef.current.contains(event.target)
      ) {
        setShowSummarySortDropdown(false);
      }
      if (
        accountSortDropdownRef.current &&
        !accountSortDropdownRef.current.contains(event.target)
      ) {
        setShowAccountSortDropdown(false);
      }
      if (
        accountPageSizeDropdownRef.current &&
        !accountPageSizeDropdownRef.current.contains(event.target)
      ) {
        setShowAccountPageSizeDropdown(false);
      }
      if (
        accountTypeDropdownRef.current &&
        !accountTypeDropdownRef.current.contains(event.target)
      ) {
        setShowAccountTypeDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (openFilterPanel !== "ACCOUNTS") return;

    const timer = setTimeout(() => {
      if (accountFilterFocusTarget === "role" && accountRoleRef.current) {
        accountRoleRef.current.focus();
        return;
      }
      if (accountFilterFocusTarget === "type" && accountTypeRef.current) {
        accountTypeRef.current.focus();
        return;
      }
      if (accountFilterFocusTarget === "status") {
        setShowStatusDropdown(true);
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [openFilterPanel, accountFilterFocusTarget]);

  useEffect(() => {
    if (openFilterPanel !== "SIGN INS") return;

    const timer = setTimeout(() => {
      if (signinFilterFocusTarget === "from" && signinFromRef.current) {
        signinFromRef.current.focus();
        return;
      }
      if (signinFilterFocusTarget === "to" && signinToRef.current) {
        signinToRef.current.focus();
        return;
      }
      if (signinFilterFocusTarget === "status") {
        setShowSigninStatusDropdown(true);
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [openFilterPanel, signinFilterFocusTarget]);

  // Derived
  const getTotalPages = () => {
    switch (selectedTab) {
      case "ACCOUNTS":
        return Math.max(1, Math.ceil(accountsTotal / accountsPageSize));
      case "ROLES":
        return Math.max(1, Math.ceil(rolesTotal / otherTabsPageSize));
      case "SESSIONS":
        return Math.max(1, Math.ceil(sessionsTotal / otherTabsPageSize));
      case "SIGN INS":
        return Math.max(1, Math.ceil(signinsTotal / otherTabsPageSize));
      case "IDENTIFICATIONS":
        return Math.max(1, Math.ceil(identificationsTotal / otherTabsPageSize));
      case "CLIENT DEVICES":
        return Math.max(1, Math.ceil(trustedDevicesTotal / otherTabsPageSize));
      case "ONE TIME PASSWORDS":
        return Math.max(1, Math.ceil(otpsTotal / otherTabsPageSize));
      default:
        return 1;
    }
  };
  const totalPages = getTotalPages();

  // Build a compact page list: always show first, last, current, previous and next pages.
  const getPageItems = (cur, total) => {
    cur = Number(cur) || 1;
    total = Math.max(1, Number(total) || 1);
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    const pagesSet = new Set();
    pagesSet.add(1);
    pagesSet.add(total);
    pagesSet.add(cur);
    if (cur - 1 > 1) pagesSet.add(cur - 1);
    if (cur + 1 < total) pagesSet.add(cur + 1);
    // Ensure neighbors around ends for small cur values
    if (cur === 1) pagesSet.add(2);
    if (cur === total) pagesSet.add(total - 1);

    const pages = Array.from(pagesSet).map(Number).filter(n => n >= 1 && n <= total).sort((a, b) => a - b);

    const out = [];
    let last = 0;
    for (const p of pages) {
      if (last && p - last > 1) out.push('...');
      out.push(p);
      last = p;
    }
    return out;
  };

  // Client-side sort fallback to keep UI in sync instantly
  const sortedAccounts = (() => {
    const arr = [...accounts];
    const get = (row) => {
      if (sortKey === "name") return (row.name || "").toLowerCase();
      if (sortKey === "type") return (row.type || "").toLowerCase();
      if (sortKey === "signin")
        return row.signin_date ? new Date(row.signin_date).getTime() : 0;
      if (sortKey === "created")
        return row.create_date ? new Date(row.create_date).getTime() : 0;
      return "";
    };
    arr.sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  })();

  const sortArray = (rows, key, dir, getter) => {
    const arr = [...rows];
    const get = getter || ((row) => row[key]);
    arr.sort((a, b) => {
      const av = get(a) ?? "";
      const bv = get(b) ?? "";
      const aVal = typeof av === "string" ? av.toLowerCase() : av;
      const bVal = typeof bv === "string" ? bv.toLowerCase() : bv;
      if (aVal < bVal) return dir === "asc" ? -1 : 1;
      if (aVal > bVal) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  };

  // Server-side sorting, so use data directly
  const sortedRoles = roles;
  const sortedSessions = sessions;
  const sortedApiKeys = apiKeys;
  const sortedSignins = signins;
  const sortedIdentifications = identifications;
  const sortedTrustedDevices = trustedDevices;
  const sortedOtps = otps;

  const handleDeleteOtp = async (otpId) => {
    try {
      const confirmed = await showConfirm(
        "Are you sure you want to delete this one-time password?",
        {
          title: "Delete One Time Password",
          confirmText: "Delete",
          cancelText: "Cancel",
        },
      );
      if (!confirmed) return;

      const res = await fetch(`${BASE_URL}/api/admin/otps/${otpId}`, {
        method: "DELETE",
        credentials: "include",
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || !payload.success) {
        throw new Error(payload.message || "Failed to delete OTP");
      }

      showSuccess("One time password deleted successfully");
      setOtps((prev) => prev.filter((r) => r.otp_id !== otpId));
      setOtpsTotal((prev) => Math.max(0, Number(prev || 0) - 1));
    } catch (err) {
      showError(err.message || "Failed to delete OTP");
    }
  };

  const formatDuration = (totalSeconds) => {
    if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "";
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);
    return parts.join(" ");
  };

  const getSessionDurationLabel = (row) => {
    const startMs = row.create_date ? new Date(row.create_date).getTime() : NaN;
    if (!Number.isFinite(startMs)) return "";
    const nowMs = Date.now();
    const isActive =
      row.state === "active" &&
      (!row.expiry_date || new Date(row.expiry_date).getTime() > nowMs);
    const endMs = isActive
      ? nowMs
      : row.expiry_date
        ? new Date(row.expiry_date).getTime()
        : nowMs;
    const seconds = Math.floor((endMs - startMs) / 1000);
    const label = formatDuration(seconds);
    return label;
  };

  const getOsIcon = (osName, osType) => {
    const name = (osName || osType || "").toLowerCase();
    if (name.includes("windows")) return windowsIcon;
    if (name.includes("mac") || name.includes("ios")) return macIcon;
    if (name.includes("android")) return androidIcon;
    return windowsIcon;
  };

  const getPlatformIcon = (deviceType, osName, osType) => {
    const os = `${osName || ""} ${osType || ""}`.toLowerCase();
    const type = String(deviceType || "").toLowerCase();
    if (os.includes("ios") || os.includes("iphone") || os.includes("ipad"))
      return iosIcon;
    if (os.includes("android")) return androidIcon;
    if (os.includes("mac") || os.includes("osx")) return macIcon;
    if (os.includes("windows") || os.includes("win")) return windowsIcon;
    if (type.includes("personal") || type.includes("mobile") || type.includes("phone"))
      return androidIcon;
    return windowsIcon;
  };

  const getBrowserIcon = (browserType) => {
    const b = (browserType || "").toLowerCase();
    if (b.includes("chrome")) return chromeIcon;
    if (b.includes("safari")) return safariIcon;
    if (b.includes("firefox")) return firefoxIcon;
    if (b.includes("edge")) return edgeIcon;
    return chromeIcon;
  };

  const closeImpersonateDialog = () => {
    setImpersonateDialog({ open: false, accountId: null });
    setImpersonationCopyUrl("");
    setImpersonationCopied(false);
  };

  const copyImpersonationUrl = async () => {
    if (!impersonationCopyUrl) return;
    try {
      await navigator.clipboard.writeText(impersonationCopyUrl);
      setImpersonationCopied(true);
      showSuccess("Impersonation URL copied");
    } catch (_) {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = impersonationCopyUrl;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(textArea);
        if (!copied) throw new Error("Copy failed");
        setImpersonationCopied(true);
        showSuccess("Impersonation URL copied");
      } catch {
        showError("Failed to copy URL. Please copy it manually.");
      }
    }
  };

  const confirmImpersonation = async () => {
    if (!impersonateDialog.accountId) return;
    try {
      setImpersonateBusy(true);
      if (impersonateMode === "new") {
        const link = await generateImpersonationLink(
          API,
          impersonateDialog.accountId
        );
        setImpersonationCopyUrl(link);
        setImpersonationCopied(false);
      } else {
        await impersonateAccount(API, impersonateDialog.accountId);
        closeImpersonateDialog();
      }
    } catch (err) {
      showError(err?.response?.data?.message || err?.message || "Impersonation failed");
    } finally {
      setImpersonateBusy(false);
    }
  };

  const formatCount = (value) =>
    Number(value || 0).toLocaleString("en-US");

  const accountsFrom =
    selectedTab === "ACCOUNTS" && accountsTotal > 0
      ? (currentPage - 1) * accountsPageSize + 1
      : 0;
  const accountsTo =
    selectedTab === "ACCOUNTS" && accountsTotal > 0
      ? Math.min(currentPage * accountsPageSize, accountsTotal)
      : 0;
  const selectedTotalCount =
    selectedTab === "ACCOUNTS"
      ? accountsTotal
      : selectedTab === "ROLES"
        ? rolesTotal
        : selectedTab === "SESSIONS"
          ? sessionsTotal
          : selectedTab === "SIGN INS"
            ? signinsTotal
            : selectedTab === "IDENTIFICATIONS"
              ? identificationsTotal
              : selectedTab === "CLIENT DEVICES"
                ? trustedDevicesTotal
                : selectedTab === "ONE TIME PASSWORDS"
                  ? otpsTotal
                  : 0;
  const selectedPageLimit =
    selectedTab === "ACCOUNTS" ? accountsPageSize : otherTabsPageSize;
  const selectedFrom =
    selectedTotalCount > 0 ? (currentPage - 1) * selectedPageLimit + 1 : 0;
  const selectedTo =
    selectedTotalCount > 0
      ? Math.min(currentPage * selectedPageLimit, selectedTotalCount)
      : 0;
  const chips = (() => {
    if (selectedTab === "ACCOUNTS") {
      return buildAdminAccountsFilterChips({ rolesFilterBy, typeFilterBy, selectedStatuses, showDeleted, sortDir });
    } else if (selectedTab === "ROLES") {
      return buildAdminRolesFilterChips({ levelFilterBy, showDeleted, sortDir: rolesSortDir });
    } else if (selectedTab === "SESSIONS") {
      return buildAdminSessionsFilterChips({ sortDir: sessionsSortDir });
    } else if (selectedTab === "SIGN INS") {
      return buildAdminSigninsFilterChips({ fromDate, toDate, selectedSigninStatuses, sortDir: signinsSortDir });
    } else if (selectedTab === "IDENTIFICATIONS") {
      return buildAdminIdentificationsFilterChips({ deviceTypeFilter, sortDir: identSortDir });
    } else if (selectedTab === "CLIENT DEVICES") {
      return buildAdminTrustedDevicesFilterChips({ trustedDeviceType, trustedFromDate, trustedToDate, trustedShowDeleted, sortDir: trustedSortDir });
    } else if (selectedTab === "ONE TIME PASSWORDS") {
      return buildAdminOtpsFilterChips({ otpsFromDate, otpsToDate, sortDir: otpsSortDir });
    }
    return [];
  })();

  const searchPlaceholder = (() => {
    switch (selectedTab) {
      case "ACCOUNTS": return "Search accounts...";
      case "ROLES": return "Search roles...";
      case "SESSIONS": return "Search sessions...";
      case "SIGN INS": return "Search sign ins...";
      case "IDENTIFICATIONS": return "Search identifications...";
      case "CLIENT DEVICES": return "Search trusted devices...";
      case "ONE TIME PASSWORDS": return "Search passwords...";
      default: return "Search...";
    }
  })();

  const isAnyFilterPanelOpen = Boolean(openFilterPanel);

  useEffect(() => {
    setShowSummarySortDropdown(false);
  }, [selectedTab]);

  return (
    <Layout active="Accounts">
      <fieldset
        disabled={adminReadOnly}
        className="border-0 m-0 p-0 min-w-0 flex flex-col flex-1 min-h-0"
      >
      <div className="py-3 px-2 flex flex-col min-h-screen no-mobile-table-stack">
        <AtlasPageHeader breadcrumb="Admin" title="Accounts" showBack={false} className="mb-2" />
        <AtlasPageDivider />

        <div className="mt-6 mx-6 p-4 border-2 border-gray-200 rounded-xl h-full flex flex-col">
          <div className={`flex-1 flex flex-col min-h-0 min-w-0 transition-all duration-300 ${filterPanel.contentOffsetClass}`}>
            {/* Tabs */}
            <AtlasTabBar
              tabs={[
                "ACCOUNTS",
                "ROLES",
                "SESSIONS",
                "SIGN INS",
                "IDENTIFICATIONS",
                "CLIENT DEVICES",
                "ONE TIME PASSWORDS",
              ]}
              activeTab={selectedTab}
              onTabChange={setSelectedTab}
            />

          <AtlasListToolbar
            searchValue={searchTerm}
            onSearchChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            searchPlaceholder={searchPlaceholder}
            onFilterClick={filterPanel.toggle}
            filterOpen={filterPanel.isOpen}
            onRefresh={() => setRefreshTick((t) => t + 1)}
            refreshSpinning={isLoading}
            resultsMeta={{
              startRow: selectedTab === "ACCOUNTS" ? accountsFrom : selectedFrom,
              endRow: selectedTab === "ACCOUNTS" ? accountsTo : selectedTo,
              totalCount: selectedTotalCount,
              pageSize: selectedPageLimit,
              pageSizeOptions: [10, 25, 50, 100, 200],
              onPageSizeChange: (size) => {
                if (selectedTab === "ACCOUNTS") {
                  setAccountsPageSize(size);
                } else {
                  setOtherTabsPageSize(size);
                }
                setCurrentPage(1);
              },
              sortBy: selectedTab === "ACCOUNTS" ? sortKey : (selectedTab === "ROLES" ? rolesSortKey : (selectedTab === "SESSIONS" ? sessionsSortKey : (selectedTab === "SIGN INS" ? signinsSortKey : (selectedTab === "IDENTIFICATIONS" ? identSortKey : (selectedTab === "CLIENT DEVICES" ? trustedSortKey : (selectedTab === "ONE TIME PASSWORDS" ? otpsSortKey : "")))))),
              sortOrder: selectedTab === "ACCOUNTS" ? sortDir : (selectedTab === "ROLES" ? rolesSortDir : (selectedTab === "SESSIONS" ? sessionsSortDir : (selectedTab === "SIGN INS" ? signinsSortDir : (selectedTab === "IDENTIFICATIONS" ? identSortDir : (selectedTab === "CLIENT DEVICES" ? trustedSortDir : (selectedTab === "ONE TIME PASSWORDS" ? otpsSortDir : "")))))),
              sortOptions: selectedTab === "ACCOUNTS" ? ACCOUNTS_SORT_OPTIONS : (selectedTab === "ROLES" ? ROLES_SORT_OPTIONS : (selectedTab === "SESSIONS" ? SESSIONS_SORT_OPTIONS : (selectedTab === "SIGN INS" ? SIGNINS_SORT_OPTIONS : (selectedTab === "IDENTIFICATIONS" ? IDENTIFICATIONS_SORT_OPTIONS : (selectedTab === "CLIENT DEVICES" ? TRUSTED_SORT_OPTIONS : (selectedTab === "ONE TIME PASSWORDS" ? OTPS_SORT_OPTIONS : [])))))),
              onSortByChange: (value) => {
                if (selectedTab === "ACCOUNTS") setSortKey(value);
                else if (selectedTab === "ROLES") setRolesSortKey(value);
                else if (selectedTab === "SESSIONS") setSessionsSortKey(value);
                else if (selectedTab === "SIGN INS") setSigninsSortKey(value);
                else if (selectedTab === "IDENTIFICATIONS") setIdentSortKey(value);
                else if (selectedTab === "CLIENT DEVICES") {
                  setTrustedSortKey(value);
                  setTrustedSortDir(
                    value === "create_date" || value === "signin_date"
                      ? "desc"
                      : "asc",
                  );
                }
                else if (selectedTab === "ONE TIME PASSWORDS") setOtpsSortKey(value);
                setCurrentPage(1);
              },
              onSortOrderToggle: () => {
                if (selectedTab === "ACCOUNTS") setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                else if (selectedTab === "ROLES") setRolesSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                else if (selectedTab === "SESSIONS") setSessionsSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                else if (selectedTab === "SIGN INS") setSigninsSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                else if (selectedTab === "IDENTIFICATIONS") setIdentSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                else if (selectedTab === "CLIENT DEVICES") setTrustedSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                else if (selectedTab === "ONE TIME PASSWORDS") setOtpsSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                setCurrentPage(1);
              },
            }}
            actions={
              <div className="flex gap-2">
                {selectedTab === "ACCOUNTS" && (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowInvites(true)}
                      className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary whitespace-nowrap"
                    >
                      VIEW INVITES
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreate(true)}
                      className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap bg-sidebar-active hover:bg-red-600"
                    >
                      CREATE
                    </button>
                  </>
                )}
                {selectedTab === "ROLES" && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!isSuperAdmin) {
                        showError("Only super admin can create/make changes");
                        return;
                      }
                      setShowCreateRole(true);
                    }}
                    className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap bg-sidebar-active hover:bg-red-600"
                  >
                    CREATE
                  </button>
                )}
                {selectedTab === "ONE TIME PASSWORDS" && (
                  <button
                    type="button"
                    onClick={() => setShowVerifyOtpCreate(true)}
                    className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap bg-sidebar-active hover:bg-red-600"
                  >
                    CREATE
                  </button>
                )}
                {selectedTab === "CLIENT DEVICES" && selectedTrustedDeviceIds.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={bulkDeleteTrustedDevices}
                      className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap bg-sidebar-active hover:bg-red-600"
                    >
                      DELETE SELECTED
                    </button>
                    <button
                      type="button"
                      onClick={bulkSignOutTrustedDevices}
                      className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary whitespace-nowrap"
                    >
                      SIGN OUT SELECTED
                    </button>
                  </>
                )}
              </div>
            }
            chipBar={
              chips.length > 0 ? (
                <AtlasFilterChipBar
                  chips={chips}
                  onChipClick={(chip) => {
                    if (chip.fieldId) filterPanel.focusField(chip.fieldId);
                    else filterPanel.open();
                  }}
                  onRemove={handleRemoveChip}
                  onClearAll={handleClearAllChips}
                />
              ) : null
            }
          />

          <AtlasFilterPanel
            isOpen={filterPanel.isOpen}
            onClose={filterPanel.close}
            onApply={() => {
              setCurrentPage(1);
              filterPanel.close();
            }}
            onClear={handleClearAllChips}
          >
            {selectedTab === "ACCOUNTS" && (
              <AdminAccountsFilterFields
                focusedFieldId={filterPanel.focusedFieldId}
                onFocusClear={filterPanel.clearFocusedField}
                rolesFilterBy={rolesFilterBy}
                setRolesFilterBy={setRolesFilterBy}
                typeFilterBy={typeFilterBy}
                setTypeFilterBy={setTypeFilterBy}
                selectedStatuses={selectedStatuses}
                setSelectedStatuses={setSelectedStatuses}
                showDeleted={showDeleted}
                setShowDeleted={setShowDeleted}
                sortKey={sortKey}
                setSortKey={setSortKey}
                sortDir={sortDir}
                setSortDir={setSortDir}
                availableRoles={availableRoles}
              />
            )}
            {selectedTab === "ROLES" && (
              <AdminRolesFilterFields
                focusedFieldId={filterPanel.focusedFieldId}
                onFocusClear={filterPanel.clearFocusedField}
                levelFilterBy={levelFilterBy}
                setLevelFilterBy={setLevelFilterBy}
                showDeleted={showDeleted}
                setShowDeleted={setShowDeleted}
                sortKey={rolesSortKey}
                setSortKey={setRolesSortKey}
                sortDir={rolesSortDir}
                setSortDir={setRolesSortDir}
                availableRoleLevels={availableRoleLevels}
              />
            )}
            {selectedTab === "SESSIONS" && (
              <AdminSessionsFilterFields
                focusedFieldId={filterPanel.focusedFieldId}
                onFocusClear={filterPanel.clearFocusedField}
                sortKey={sessionsSortKey}
                setSortKey={setSessionsSortKey}
                sortDir={sessionsSortDir}
                setSortDir={setSessionsSortDir}
              />
            )}
            {selectedTab === "SIGN INS" && (
              <AdminSigninsFilterFields
                focusedFieldId={filterPanel.focusedFieldId}
                onFocusClear={filterPanel.clearFocusedField}
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                selectedSigninStatuses={selectedSigninStatuses}
                setSelectedSigninStatuses={setSelectedSigninStatuses}
                sortKey={signinsSortKey}
                setSortKey={setSigninsSortKey}
                sortDir={signinsSortDir}
                setSortDir={setSigninsSortDir}
              />
            )}
            {selectedTab === "IDENTIFICATIONS" && (
              <AdminIdentificationsFilterFields
                focusedFieldId={filterPanel.focusedFieldId}
                onFocusClear={filterPanel.clearFocusedField}
                deviceTypeFilter={deviceTypeFilter}
                setDeviceTypeFilter={setDeviceTypeFilter}
                sortKey={identSortKey}
                setSortKey={setIdentSortKey}
                sortDir={identSortDir}
                setSortDir={setIdentSortDir}
              />
            )}
            {selectedTab === "CLIENT DEVICES" && (
              <AdminTrustedDevicesFilterFields
                focusedFieldId={filterPanel.focusedFieldId}
                onFocusClear={filterPanel.clearFocusedField}
                trustedDeviceType={trustedDeviceType}
                setTrustedDeviceType={setTrustedDeviceType}
                trustedFromDate={trustedFromDate}
                setTrustedFromDate={setTrustedFromDate}
                trustedToDate={trustedToDate}
                setTrustedToDate={setTrustedToDate}
                trustedShowDeleted={trustedShowDeleted}
                setTrustedShowDeleted={setTrustedShowDeleted}
                sortKey={trustedSortKey}
                setSortKey={(value) => {
                  setTrustedSortKey(value);
                  setTrustedSortDir(
                    value === "create_date" || value === "signin_date"
                      ? "desc"
                      : "asc",
                  );
                }}
                sortDir={trustedSortDir}
                setSortDir={setTrustedSortDir}
              />
            )}
                        {selectedTab === "ONE TIME PASSWORDS" && (
              <AdminOtpsFilterFields
                focusedFieldId={filterPanel.focusedFieldId}
                onFocusClear={filterPanel.clearFocusedField}
                otpsFromDate={otpsFromDate}
                setOtpsFromDate={setOtpsFromDate}
                otpsToDate={otpsToDate}
                setOtpsToDate={setOtpsToDate}
                sortKey={otpsSortKey}
                setSortKey={setOtpsSortKey}
                sortDir={otpsSortDir}
                setSortDir={setOtpsSortDir}
              />
            )}
          </AtlasFilterPanel>

          {/* Table for ACCOUNTS */}
          {selectedTab === "ACCOUNTS" && (
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow table-force-rows">
              <table className="w-full min-w-[900px] table-fixed border-collapse">
                <thead className="bg-[#dc1f0080] text-white">
                  <tr className="text-left select-none">
                    <th className="text-sm font-medium px-4 py-2 w-[28%]">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: '100%' }}
                        onClick={() => handleSort("name")}
                        title="Sort by Name"
                      >
                        <span>Name</span>
                        {sortKey === "name" && <SortArrow colKey="name" activeKey={sortKey} activeDir={sortDir} />}
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[14%]">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: '100%' }}
                        onClick={() => handleSort("type")}
                        title="Sort by Type"
                      >
                        <span>Type</span>
                        {sortKey === "type" && <SortArrow colKey="type" activeKey={sortKey} activeDir={sortDir} />}
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[25%]">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: '100%' }}
                        onClick={() => handleSort("roles")}
                        title="Sort by Roles"
                      >
                        <span>Roles</span>
                        {sortKey === "roles" && <SortArrow colKey="roles" activeKey={sortKey} activeDir={sortDir} />}
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[14%]">
                      <button
                        type="button"
                        className="flex items-center gap-2 whitespace-nowrap group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: '100%' }}
                        onClick={() => handleSort("last_sign_in")}
                        title="Sort by Last Sign-In"
                      >
                        <span className="whitespace-nowrap">Last Sign-In</span>
                        {sortKey === "last_sign_in" && <SortArrow colKey="last_sign_in" activeKey={sortKey} activeDir={sortDir} />}
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[14%]">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: '100%' }}
                        onClick={() => handleSort("created")}
                        title="Sort by Created"
                      >
                        <span>Created</span>
                        <SortArrow colKey="created" />
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[5%]"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {isLoading && (
                    <tr><td className="px-4 py-2 text-sm" colSpan={6}>Loading...</td></tr>
                  )}
                  {error && !isLoading && (
                    <tr><td className="px-4 py-2 text-sm text-red-600" colSpan={6}>{error}</td></tr>
                  )}
                  {!isLoading && !error && accounts.length === 0 && (
                    <tr><td className="px-4 py-2 text-sm" colSpan={6}>No records</td></tr>
                  )}
                  {!isLoading && !error && sortedAccounts.map((row, index) => (
                    <tr
                      key={row.account_id}
                      className={`text-left text-base hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      <td className="px-4 py-2.5 whitespace-normal break-words">
                        <div className="flex items-center gap-3">
                          <StaffAvatarCircle
                            accountId={row.account_id}
                            avatarSrc={avatarSrcForAccount(row.account_id)}
                            allowProxyFallback={false}
                            employee={employeeRowForAvatar(row)}
                            className="w-8 h-8"
                          />
                          <div className="flex flex-col whitespace-normal break-words max-w-[60vw] md:max-w-none">
                            <span
                              className="font-medium cursor-pointer text-[#DC1F00]"
                              onClick={() =>
                                navigate("/account", {
                                  state: {
                                    userName: row.name,
                                    accountId: row.account_id || row.id,
                                  },
                                })
                              }
                            >
                              {row.name}
                            </span>
                            <span className="text-sm text-textGray">
                              {row.email}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {row.type || ""}
                      </td>
                      <td className="px-3 py-2.5 whitespace-normal break-words">
                        <div className="flex flex-wrap gap-1.5 max-w-full">
                          {row.roles ? (
                            row.roles.split(",").map((role, idx) => {
                              const trimmed = role.trim();
                              if (!trimmed) return null;
                              return (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200"
                                >
                                  {trimmed}
                                </span>
                              );
                            })
                          ) : (
                            ""
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-sm">
                        {formatLastSignin(row.signin_date)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-sm">
                        {formatLastSignin(row.create_date)}
                      </td>
                      <td className="py-2.5">
                        {!showDeleted && String(row.state || "").toLowerCase() !== "deleted" && (
                          <ActionDropdown
                            actions={[
                              {
                                label: "Impersonate",
                                // No onClick renders the item disabled (Super Admins / Admins only)
                                onClick: canImpersonate
                                  ? () => {
                                      setImpersonateMode("new");
                                      setImpersonationCopyUrl("");
                                      setImpersonationCopied(false);
                                      setImpersonateDialog({
                                        open: true,
                                        accountId: row.account_id,
                                      });
                                    }
                                  : undefined,
                              },
                              {
                                label: "Delete",
                                danger: true,
                                onClick: async () => {
                                  try {
                                    const confirmed = await showConfirm(
                                      "Are you sure you want to delete this user?"
                                    );
                                    if (!confirmed) return;

                                    const res = await fetch(
                                      `${BASE_URL}/api/admin/accounts/${row.account_id}`,
                                      {
                                        method: "DELETE",
                                        credentials: "include",
                                      }
                                    );
                                    const data = await res.json();
                                    if (!res.ok)
                                      throw new Error(
                                        data.message || "Failed to delete account"
                                      );
                                    showSuccess("Account deleted successfully");
                                    setRefreshTick((t) => t + 1);
                                  } catch (err) {
                                    showError(
                                      err.message || "Failed to delete account"
                                    );
                                  }
                                },
                              },
                            ]}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Table for ROLES */}
          {selectedTab === "ROLES" && (
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow">
              <table className="w-full min-w-[800px] table-fixed border-collapse">
                <thead className="bg-[#dc1f0080] text-white">
                  <tr className="text-left select-none">
                    <th className="text-sm font-medium px-4 py-2 w-1/3">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: "100%" }}
                        onClick={() => {
                          if (rolesSortKey === "name") {
                            setRolesSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                          } else {
                            setRolesSortKey("name");
                            setRolesSortDir("asc");
                          }
                        }}
                        title="Sort by Name"
                      >
                        <span>Name</span>
                        <SortArrow colKey="name" activeKey={rolesSortKey} activeDir={rolesSortDir} onChange={(k, d) => { setRolesSortKey(k); setRolesSortDir(d); }} />
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-1/8">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: "100%" }}
                        onClick={() => {
                          if (rolesSortKey === "level") {
                            setRolesSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                          } else {
                            setRolesSortKey("level");
                            setRolesSortDir("asc");
                          }
                        }}
                        title="Sort by Level"
                      >
                        <span>Level</span>
                        <SortArrow colKey="level" activeKey={rolesSortKey} activeDir={rolesSortDir} onChange={(k, d) => { setRolesSortKey(k); setRolesSortDir(d); }} />
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-1/2">Permissions</th>
                    <th className="text-sm font-medium px-3 py-2 w-[3%]"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {isLoading && (
                    <tr><td className="px-4 py-2 text-sm" colSpan={4}>Loading...</td></tr>
                  )}
                  {error && !isLoading && (
                    <tr><td className="px-4 py-2 text-sm text-red-600" colSpan={4}>{error}</td></tr>
                  )}
                  {!isLoading && !error && roles.length === 0 && (
                    <tr><td className="px-4 py-2 text-sm" colSpan={4}>No records</td></tr>
                  )}
                  {!isLoading && !error && sortedRoles.map((row, index) => (
                    <tr key={row.role_id} className={`text-left text-base hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-4 py-2.5 font-medium">
                        <button
                          type="button"
                          onClick={() => {
                            if (!isSuperAdmin) {
                              showError("Only super admin can create/make changes");
                              return;
                            }
                            setEditRole(row);
                          }}
                          className="text-sm text-[#DC1F00] font-medium hover:underline focus:outline-none"
                          title={`Edit role ${row.name}`}
                        >
                          {row.name}
                        </button>
                      </td>
                      <td className="px-4 py-2.5">{row.level}</td>
                      <td className="px-4 py-2.5">
                        <PermissionsCell rawPermissions={row.raw_permissions} />
                      </td>
                      <td className="py-2.5">
                        {!showDeleted && <ActionDropdown
                          actions={[
                            {
                              label: "Edit",
                              onClick: () => {
                                if (!isSuperAdmin) {
                                  showError("Only super admin can create/make changes");
                                  return;
                                }
                                setEditRole(row);
                              },
                            },
                            {
                              label: "Delete",
                              onClick: async () => {
                                if (!isSuperAdmin) {
                                  showError("Only super admin can create/make changes");
                                  return;
                                }
                                try {
                                  const confirmed = await showConfirm(
                                    `Are you sure you want to delete role "${row.name}"?`
                                  );
                                  if (!confirmed) return;
                                  const token = localStorage.getItem("token");
                                  const res = await fetch(
                                    `${BASE_URL}/api/acl/roles/${row.role_id}`,
                                    {
                                      method: "DELETE",
                                      credentials: "include",
                                      headers: {
                                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                                      },
                                    }
                                  );
                                  const j = await res.json().catch(() => ({}));
                                  if (!res.ok)
                                    throw new Error(
                                      j.message || "Failed to delete role"
                                    );
                                  showSuccess("Role deleted successfully");
                                  setRefreshTick((t) => t + 1);
                                } catch (err) {
                                  showError(err.message || "Failed to delete role");
                                }
                              },
                            },
                          ]}
                        />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Table for SESSIONS */}
          {selectedTab === "SESSIONS" && (
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow">
              <table className="w-full table-fixed border-collapse min-w-[800px]">
                <thead className="bg-[#dc1f0080] text-white">
                  <tr className="text-left select-none">
                    <th className="text-sm font-medium px-4 py-2 w-2/5">
                      <span>Account</span>
                      {/* <SortArrow colKey="name" activeKey={sessionsSortKey} activeDir={sessionsSortDir} onChange={(k,d)=>{setSessionsSortKey(k);setSessionsSortDir(d);}} /> */}
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[10%]">
                      <span>Status</span>
                      {/* <SortArrow colKey="state" activeKey={sessionsSortKey} activeDir={sessionsSortDir} onChange={(k,d)=>{setSessionsSortKey(k);setSessionsSortDir(d);}} /> */}
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[15%]">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: "100%" }}
                        onClick={() => {
                          if (sessionsSortKey === "create_date") setSessionsSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                          else { setSessionsSortKey("create_date"); setSessionsSortDir("asc"); }
                        }}
                        title="Sort by Create Date"
                      >
                        <span>Created</span>
                        <SortArrow colKey="create_date" activeKey={sessionsSortKey} activeDir={sessionsSortDir} onChange={(k, d) => { setSessionsSortKey(k); setSessionsSortDir(d); }} />
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[15%]">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: "100%" }}
                        onClick={() => {
                          if (sessionsSortKey === "expiry_date") setSessionsSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                          else { setSessionsSortKey("expiry_date"); setSessionsSortDir("asc"); }
                        }}
                        title="Sort by Expiry Date"
                      >
                        <span>Expires</span>
                        <SortArrow colKey="expiry_date" activeKey={sessionsSortKey} activeDir={sessionsSortDir} onChange={(k, d) => { setSessionsSortKey(k); setSessionsSortDir(d); }} />
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[15%]">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {isLoading && (
                    <tr><td className="px-4 py-2 text-sm" colSpan={5}>Loading...</td></tr>
                  )}
                  {error && !isLoading && (
                    <tr><td className="px-4 py-2 text-sm text-red-600" colSpan={5}>{error}</td></tr>
                  )}
                  {!isLoading && !error && sessions.length === 0 && (
                    <tr><td className="px-4 py-2 text-sm" colSpan={5}>No records</td></tr>
                  )}
                  {!isLoading && !error && sortedSessions.map((row, index) => (
                    <tr key={row.session_id} className={`text-left text-base hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-4 py-2.5 whitespace-normal break-words">
                        <div className="flex items-center gap-3">
                          <StaffAvatarCircle
                            accountId={row.account_id || row.id}
                            avatarSrc={avatarSrcForAccount(row.account_id || row.id)}
                            allowProxyFallback={false}
                            employee={employeeRowForAvatar(row)}
                            className="w-6 h-6"
                          />
                          <div className="flex flex-col whitespace-normal break-words max-w-[60vw] md:max-w-none">
                            <span
                              className="font-medium break-words whitespace-normal cursor-pointer text-[#DC1F00]"
                              onClick={() =>
                                navigate("/account", {
                                  state: {
                                    userName: row.name,
                                    accountId: row.account_id || row.id,
                                  },
                                })
                              }
                            >
                              {row.name}
                            </span>
                            <span className="text-sm text-textGray">
                              {row.email}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-3 h-3 rounded-full ${row.state === "active"
                              ? "bg-green-500"
                              : "bg-red-500"
                              }`}
                          ></span>
                          <span>{row.state}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-sm whitespace-normal break-words">
                        {row.create_date
                          ? formatAppDateTime(row.create_date)
                          : ""}
                      </td>
                      <td className="px-3 py-2.5 text-sm whitespace-normal break-words">
                        {row.expiry_date
                          ? formatAppDateTime(row.expiry_date)
                          : ""}
                      </td>
                      <td className="px-3 py-2.5 text-sm whitespace-nowrap">
                        {getSessionDurationLabel(row)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* { Table for API KEYS */}
          {/* {selectedTab === "API KEYS" && (
              <table className="w-full table-fixed border-collapse border shadow">
                <thead className="bg-[#dc1f0080] text-white">
                  <tr className="text-left select-none">
                    <th className="border text-sm font-medium pl-6 py-3 rounded-tl-lg w-1/4">
                      <span>Key</span>
                    </th>
                    <th className="border text-sm font-medium px-4 py-3 w-1/4">
                      <span>Account</span>
                      <SortArrow colKey="account_id" activeKey={apiKeysSortKey} activeDir={apiKeysSortDir} onChange={(k,d)=>{setApiKeysSortKey(k);setApiKeysSortDir(d);}} />
                    </th>
                    <th className="border text-sm font-medium px-4 py-3 w-1/4">
                      <span>Status</span>
                      <SortArrow colKey="state" activeKey={apiKeysSortKey} activeDir={apiKeysSortDir} onChange={(k,d)=>{setApiKeysSortKey(k);setApiKeysSortDir(d);}} />
                    </th>
                    <th className="border text-sm font-medium px-4 py-3 w-1/4">
                      <span>Expiry</span>
                      <SortArrow colKey="expiry_date" activeKey={apiKeysSortKey} activeDir={apiKeysSortDir} onChange={(k,d)=>{setApiKeysSortKey(k);setApiKeysSortDir(d);}} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                   {apiKeys.length === 0 && (
                    <tr><td className="pl-6 py-3 text-sm" colSpan={4}>No records</td></tr>
                  )}
                   {sortedApiKeys.map((row) => (
                    <tr key={row.api_key_id} className="text-left text-sm">
                      <td className="pl-6 pt-2 pb-1 ">{row.key_prefix ? `${row.key_prefix}********` : ''}</td>
                      <td className="pl-4 pt-2 pb-1">{row.account_id}</td>
                      <td className="pl-4 pt-2 pb-1">{row.state}</td>
                      <td className="pl-4 pt-2 pb-1">{row.expiry_date ? formatAppDate(row.expiry_date) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )} */}

          {/* Table for SIGN INS */}
          {selectedTab === "SIGN INS" && (
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow">
              <table className="w-full table-fixed border-collapse min-w-[1000px]">
                <thead className="bg-[#dc1f0080] text-white">
                  <tr className="text-left select-none">
                    {/* Account column (narrower width) */}
                    <th className="text-sm font-medium px-4 py-2 w-[25%]">
                      <span>Account</span>
                      {/* <SortArrow
                          colKey="name"
                          activeKey={signinsSortKey}
                          activeDir={signinsSortDir}
                          onChange={(k, d) => {
                            setSigninsSortKey(k);
                            setSigninsSortDir(d);
                          }}
                        /> */}
                    </th>

                    <th className="text-sm font-medium px-3 py-2 w-[12%]">
                      <span>Status</span>
                      <SortArrow
                        colKey="state"
                        activeKey={signinsSortKey}
                        activeDir={signinsSortDir}
                        onChange={(k, d) => {
                          setSigninsSortKey(k);
                          setSigninsSortDir(d);
                        }}
                      />
                    </th>

                    <th className="text-sm font-medium px-3 py-2 w-[12%]">
                      <span>Verification</span>
                    </th>

                    <th className="text-sm font-medium px-3 py-2 w-[8%] text-center">
                      <span>Device</span>
                    </th>

                    <th className="text-sm font-medium px-3 py-2 w-[12%]">
                      <span>Browser</span>
                    </th>

                    <th className="text-sm font-medium px-3 py-2 w-[18%]">
                      <span>Location</span>
                    </th>

                    {/* Date column (2-line display) */}
                    <th className="text-sm font-medium px-3 py-2 w-[13%]">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: "100%" }}
                        onClick={() => {
                          if (signinsSortKey === "signin_date") setSigninsSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                          else { setSigninsSortKey("signin_date"); setSigninsSortDir("asc"); }
                        }}
                        title="Sort by Date"
                      >
                        <span>Date</span>
                        <SortArrow
                          colKey="signin_date"
                          activeKey={signinsSortKey}
                          activeDir={signinsSortDir}
                          onChange={(k, d) => {
                            setSigninsSortKey(k);
                            setSigninsSortDir(d);
                          }}
                        />
                      </button>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {isLoading && (
                    <tr><td className="px-4 py-2 text-sm" colSpan={7}>Loading...</td></tr>
                  )}
                  {error && !isLoading && (
                    <tr><td className="px-4 py-2 text-sm text-red-600" colSpan={7}>{error}</td></tr>
                  )}
                  {!isLoading && !error && signins.length === 0 && (
                    <tr>
                      <td className="px-4 py-2 text-sm" colSpan={7}>
                        No records
                      </td>
                    </tr>
                  )}

                  {!isLoading && !error && sortedSignins.map((row, index) => {
                    const date = row.create_date
                      ? formatAppDate(row.create_date)
                      : "";
                    const time = row.create_date
                      ? formatAppTime(row.create_date)
                      : "";

                    return (
                      <tr
                        key={row.signin_id}
                        className={`text-left text-base hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }`}
                      >
                        {/* Account column (reduced width) */}
                        <td className="px-4 py-2.5 whitespace-normal break-words">
                          <div className="flex items-center gap-2">
                            <StaffAvatarCircle
                              accountId={row.account_id || row.id}
                              avatarSrc={avatarSrcForAccount(row.account_id || row.id)}
                              allowProxyFallback={false}
                              employee={employeeRowForAvatar(row)}
                              className="w-6 h-6"
                            />
                            <div className="flex flex-col">
                              <span
                                className="font-medium break-words whitespace-normal cursor-pointer text-[#DC1F00]"
                                onClick={() =>
                                  navigate("/account", {
                                    state: {
                                      userName: row.name,
                                      accountId: row.account_id || row.id,
                                    },
                                  })
                                }
                              >
                                {row.name}
                              </span>
                              <span className="text-sm text-textGray break-words whitespace-normal">
                                {row.email}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td className="px-3 py-2.5 flex items-center gap-2">
                          <span
                            className={`border border-border rounded px-2 py-0.5 ${getSigninStateColor(row.state)}`}
                          >
                            {getSigninStateLabel(row.state)}
                          </span>
                        </td>

                        <td className="px-3 py-2.5 whitespace-normal break-words">
                          {row.method || ""}
                        </td>

                        <td className="px-3 py-2.5 whitespace-normal break-words text-center">
                          <AdminSigninDeviceCell
                            deviceType={row.device_type}
                            osName={row.os_name}
                            osType={row.os_type}
                          />
                        </td>

                        <td className="px-3 py-2.5 whitespace-normal break-words">
                          <AdminSigninBrowserCell
                            browserType={row.browser_type}
                            browserVersion={row.browser_version}
                          />
                        </td>

                        <td className="px-3 py-2.5 whitespace-normal break-words">
                          <AdminSigninLocationCell
                            ipAddress={row.ip_address}
                            country={row.country}
                            region={row.region}
                            city={row.city}
                            locationLat={row.location_lat}
                            locationLng={row.location_lng}
                          />
                        </td>

                        {/* 2-line date/time */}
                        <td className="px-3 py-2.5 whitespace-normal text-sm leading-tight">
                          <div className="flex flex-col">
                            <span>{date}</span>
                            <span className="text-textGray">{time}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Table for IDENTIFICATIONS */}
          {selectedTab === "IDENTIFICATIONS" && (
            <div className="atlas-list-table-wrap overflow-x-auto min-w-0 rounded-lg border border-gray-200 shadow">
              <table className="w-full table-fixed border-collapse min-w-[1200px] atlas-identifications-table">
                <thead className="bg-[#dc1f0080] text-white">
                  <tr className="text-left select-none">
                    <th className="font-medium px-2 py-2 w-[5%]">Method</th>
                    <th className="font-medium px-2 py-2 w-[15%]">
                      <button
                        type="button"
                        className="flex items-center gap-1 min-w-0 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: "100%" }}
                        onClick={() => {
                          if (identSortKey === "name") setIdentSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                          else { setIdentSortKey("name"); setIdentSortDir("asc"); }
                        }}
                        title="Sort by Account"
                      >
                        <span className="truncate">Account</span>
                        <SortArrow colKey="name" activeKey={identSortKey} activeDir={identSortDir} onChange={(k, d) => { setIdentSortKey(k); setIdentSortDir(d); }} />
                      </button>
                    </th>
                    <th className="font-medium px-2 py-2 w-[7%]">Action</th>
                    <th className="font-medium px-2 py-2 w-[4%]">Scan</th>
                    <th className="font-medium px-2 py-2 w-[7%]">Status</th>
                    <th className="font-medium px-2 py-2 w-[11%]">
                      <button
                        type="button"
                        className="flex items-center gap-1 min-w-0 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: "100%" }}
                        onClick={() => {
                          if (identSortKey === "client_name") setIdentSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                          else { setIdentSortKey("client_name"); setIdentSortDir("asc"); }
                        }}
                        title="Sort by Client"
                      >
                        <span className="truncate">Client</span>
                        <SortArrow colKey="client_name" activeKey={identSortKey} activeDir={identSortDir} onChange={(k, d) => { setIdentSortKey(k); setIdentSortDir(d); }} />
                      </button>
                    </th>
                    <th className="font-medium px-2 py-2 w-[7%]" title="Client Devices">Devices</th>
                    <th className="font-medium px-2 py-2 w-[9%]">Device Type</th>
                    <th className="font-medium px-2 py-2 w-[16%]">Location</th>
                    <th className="font-medium px-2 py-2 w-[19%]">
                      <button
                        type="button"
                        className="flex items-center gap-1 min-w-0 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: "100%" }}
                        onClick={() => {
                          if (identSortKey === "create_date") setIdentSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                          else { setIdentSortKey("create_date"); setIdentSortDir("asc"); }
                        }}
                        title="Sort by Date"
                      >
                        <span className="truncate">Date</span>
                        <SortArrow colKey="create_date" activeKey={identSortKey} activeDir={identSortDir} onChange={(k, d) => { setIdentSortKey(k); setIdentSortDir(d); }} />
                      </button>
                    </th>

                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {isLoading && (
                    <tr><td className="px-4 py-2 text-sm" colSpan={10}>Loading...</td></tr>
                  )}
                  {error && !isLoading && (
                    <tr><td className="px-4 py-2 text-sm text-red-600" colSpan={10}>{error}</td></tr>
                  )}
                  {!isLoading && !error && identifications.length === 0 && (
                    <tr><td className="px-4 py-2 text-sm" colSpan={10}>No records</td></tr>
                  )}
                  {!isLoading && !error && sortedIdentifications.map((row, index) => (
                    <tr key={row.identification_id} className={`text-left text-sm align-top hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-2 py-2 capitalize truncate" title={row.method || ''}>{row.method || ''}</td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <StaffAvatarCircle
                            accountId={row.account_id || row.id}
                            avatarSrc={avatarSrcForAccount(row.account_id || row.id)}
                            allowProxyFallback={false}
                            employee={employeeRowForAvatar(row)}
                            className="ident-account-avatar w-7 h-7 flex-shrink-0"
                            onImageClick={(src) => {
                              setPreviewImageCaption("Profile photo");
                              setPreviewImageUrl(src);
                            }}
                          />
                          <div className="min-w-0 flex-1 overflow-hidden">
                            <div className="truncate font-medium leading-tight" title={row.name || ''}>
                              {row.name}
                            </div>
                            {row.phone ? (
                              <div className="truncate text-xs text-textGray leading-tight" title={row.phone}>
                                {row.phone}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2 overflow-hidden">
                        <div className="truncate" title={formatIdentificationAction(row.action)}>
                          {formatIdentificationAction(row.action)}
                        </div>
                        {row.shift_id ? (
                          <button
                            type="button"
                            className="text-[#DC1F00] text-left normal-case hover:underline truncate block max-w-full text-xs"
                            title={`Shift: ${row.shift_id}`}
                            onClick={() =>
                              navigate("/jobs", { state: { shiftId: row.shift_id } })
                            }
                          >
                            Shift: {row.shift_id}
                          </button>
                        ) : null}
                      </td>
                      <td className="px-2 py-2">
                        {row.scan_image_url ? (
                          <img
                            src={row.scan_image_url}
                            alt="Scan"
                            className="ident-scan-thumb w-9 h-9 rounded object-cover border border-border cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => {
                              setPreviewImageCaption("Face scan photo");
                              setPreviewImageUrl(row.scan_image_url);
                            }}
                          />
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <span
                          className={`inline-block max-w-full truncate border border-border rounded px-1.5 py-0.5 capitalize ${row.state === "success"
                            ? "text-green-600"
                            : row.state === "failed"
                              ? "text-red-600"
                              : "text-muted-foreground"
                            }`}
                          title={row.state || ""}
                        >
                          {row.state || ""}
                        </span>
                      </td>
                      <td className="px-2 py-2 overflow-hidden">
                        <div className="truncate" title={row.client_name || ''}>{row.client_name || ""}</div>
                        {row.client_location_name ? (
                          <div className="truncate text-xs text-textGray" title={row.client_location_name}>
                            {row.client_location_name}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-2 py-2">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-600 border border-gray-200 flex-shrink-0">
                          <img
                            src={getPlatformIcon(
                              row.client_device_type || row.device_type,
                              row.os_name,
                              row.os_type
                            )}
                            alt={row.client_device_type || row.device_type || "device"}
                            className="w-4 h-4 object-contain"
                          />
                        </span>
                      </td>
                      <td className="px-2 py-2 truncate capitalize" title={row.client_device_type || row.device_type || ''}>
                        {row.client_device_type || row.device_type || ""}
                      </td>
                      <td className="px-2 py-2 overflow-hidden">
                        {(() => {
                          const lat = adminLocationCoord(row.location_lat);
                          const lng = adminLocationCoord(row.location_lng);
                          const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
                          const label = row.location_formatted || row.location || "";
                          const displayIp = adminDisplayableIp(row.ip_address)
                            ? String(row.ip_address).trim()
                            : "";
                          const href = hasCoords
                            ? `https://www.google.com/maps?q=${lat},${lng}`
                            : label
                              ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}`
                              : "";
                          if (!label && !displayIp && !hasCoords) {
                            return <span className="text-muted-foreground">—</span>;
                          }
                          return (
                            <div className="min-w-0 overflow-hidden">
                              {displayIp ? (
                                <div className="truncate font-mono text-xs text-gray-500" title={displayIp}>{displayIp}</div>
                              ) : null}
                              {(label || hasCoords) && href ? (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[#DC1F00] hover:underline text-xs truncate block"
                                  title={label || `${lat.toFixed(4)}, ${lng.toFixed(4)}`}
                                >
                                  {label || `${lat.toFixed(4)}, ${lng.toFixed(4)}`}
                                </a>
                              ) : label ? (
                                <div className="truncate text-xs" title={label}>{label}</div>
                              ) : null}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-2 py-2 truncate whitespace-nowrap" title={row.create_date ? formatAppDateTime(row.create_date) : ''}>
                        {row.create_date
                          ? `${formatAppDate(row.create_date)} ${formatAppTime(row.create_date)}`
                          : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Table for CLIENT DEVICES */}
          {selectedTab === "CLIENT DEVICES" && (
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow">
              <table className="w-full table-fixed border-collapse min-w-[1100px]">
                <thead className="bg-[#dc1f0080] text-white">
                  <tr className="text-left select-none">
                    <th className="text-sm font-medium px-4 py-2 w-[8%]">Device</th>
                    <th className="text-sm font-medium px-3 py-2 w-[18%]">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: "100%" }}
                        onClick={() => handleTrustedColumnSort("client_name")}
                        title="Sort by Client"
                      >
                        <span>Client</span>
                        {trustedSortIndicator("client_name")}
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[12%]">Locations</th>
                    <th className="text-sm font-medium px-3 py-2 w-[10%]">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: "100%" }}
                        onClick={() => handleTrustedColumnSort("state")}
                        title="Sort by Device Status"
                      >
                        <span>Device Status</span>
                        {trustedSortIndicator("state")}
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[18%]">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: "100%" }}
                        onClick={() => handleTrustedColumnSort("signin_date")}
                        title="Sort by Last Signed In"
                      >
                        <span>Last Signed in by</span>
                        {trustedSortIndicator("signin_date")}
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[8%]">Device Type</th>
                    <th className="text-sm font-medium px-3 py-2 w-[14%]">Location</th>
                    <th className="text-sm font-medium px-3 py-2 w-[12%]">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: "100%" }}
                        onClick={() => handleTrustedColumnSort("create_date")}
                        title="Sort by Created"
                      >
                        <span>Created</span>
                        {trustedSortIndicator("create_date")}
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[3%]"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {isLoading && (
                    <tr><td className="px-4 py-2 text-sm" colSpan={10}>Loading...</td></tr>
                  )}
                  {error && !isLoading && (
                    <tr><td className="px-4 py-2 text-sm text-red-600" colSpan={10}>{error}</td></tr>
                  )}
                  {!isLoading && !error && trustedDevices.length === 0 && (
                    <tr><td className="px-4 py-2 text-sm" colSpan={9}>No records</td></tr>
                  )}
                  {!isLoading && !error && sortedTrustedDevices.map((row, index) => {
                    const statusMap = {
                      active: "Active",
                      unassigned: "Unassigned",
                      deleted: "Deleted",
                    };
                    const typeMap = {
                      app: "App",
                      personal: "Personal",
                    };
                    return (
                      <tr key={row.trusted_device_id} className={`text-left text-base hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-3">
                            <img
                              src={getPlatformIcon(row.device_type, row.os_name, row.os_type)}
                              alt="device"
                              className="w-6 h-6"
                            />
                          </div>
                        </td>
                        <td
                          className="px-3 py-2.5 font-medium cursor-pointer text-[#DC1F00] hover:underline"
                          onClick={() => {
                            setSelectedRow({ id: row.client_id, name: row.client_name });
                            setOpenModal("ClientDetailModal");
                          }}
                        >
                          {row.client_name || ""}
                        </td>
                        <td className="px-3 py-2.5 text-sm">
                          <div className="flex flex-col gap-1">
                            {Array.isArray(row.clientLocations) && row.clientLocations.length > 0 ? (
                              row.clientLocations.map((loc, idx) => (
                                <span key={idx} className="block text-gray-700">
                                  {loc.name || (loc.address && loc.address.name) || "Unnamed Location"}
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-sm">
                          {statusMap[row.state] || row.state || ""}
                        </td>
                        <td className="px-3 py-2.5 text-sm">
                          {row.account_id ? (
                            <div className="flex items-center gap-2">
                              <StaffAvatarCircle
                                accountId={row.account_id}
                                avatarSrc={avatarSrcForAccount(row.account_id)}
                                allowProxyFallback={false}
                                employee={employeeRowForAvatar(row)}
                                className="ident-account-avatar w-7 h-7 flex-shrink-0"
                                onImageClick={(src) => {
                                  setPreviewImageCaption("Profile photo");
                                  setPreviewImageUrl(src);
                                }}
                              />
                              <div className="flex flex-col min-w-0">
                                <span className="font-medium text-gray-900 truncate">
                                  {[row.first_name, row.last_name].filter(Boolean).join(" ")}
                                </span>
                                {row.signin_date ? (
                                  <span className="text-xs text-gray-500">
                                    {formatAppDateTime(row.signin_date)}
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-400">Never</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-sm">
                          {typeMap[row.device_type] || row.device_type || ""}
                        </td>
                        <td className="px-3 py-2.5">
                          <AdminSigninLocationCell
                            ipAddress={row.ip_address || row.ip}
                            country={row.country}
                            region={row.region}
                            city={row.city}
                            locationLat={row.location_lat}
                            locationLng={row.location_lng}
                          />
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm">
                          {row.create_date
                            ? formatAppDate(row.create_date)
                            : ""}
                        </td>
                        <td className="py-2.5">
                          {!trustedShowDeleted && <ActionDropdown
                            actions={[
                              {
                                label: "Delete",
                                danger: true,
                                onClick: async () => {
                                  if (
                                    !window.confirm(
                                      "Are you sure you want to delete this trusted device?"
                                    )
                                  )
                                    return;
                                  try {
                                    const res = await fetch(
                                      `${BASE_URL}/api/admin/trusted-devices/${row.trusted_device_id}`,
                                      {
                                        method: "DELETE",
                                        credentials: "include",
                                      }
                                    );
                                    const data = await res.json();
                                    if (!res.ok)
                                      throw new Error(
                                        data.message ||
                                        "Failed to delete device"
                                      );
                                    showSuccess("Device deleted successfully");
                                    // Refresh list
                                    setRefreshTick((t) => t + 1);
                                  } catch (err) {
                                    showError(
                                      err.message || "Failed to delete device"
                                    );
                                  }
                                },
                              },
                            ]}
                          />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {openModal === "ClientDetailModal" && (
                <ClientDetailModal
                  client={selectedRow}
                  onClose={() => setOpenModal(null)}
                  isOpen={true}
                />
              )}
            </div>
          )}

          {/* Table for ONE TIME PASSWORDS */}
          {selectedTab === "ONE TIME PASSWORDS" && (
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow">
              <table className="w-full min-w-[800px] table-fixed border-collapse">
                <thead className="bg-[#dc1f0080] text-white">
                  <tr className="text-left select-none">
                    <th className="text-sm font-medium px-4 py-2 w-2/5">
                      <span>Account</span>
                      <SortArrow
                        colKey="email"
                        activeKey={otpsSortKey}
                        activeDir={otpsSortDir}
                        onChange={(k, d) => {
                          setOtpsSortKey(k);
                          setOtpsSortDir(d);
                        }}
                      />
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[15%]">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: "100%" }}
                        onClick={() => {
                          if (otpsSortKey === "state") setOtpsSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                          else { setOtpsSortKey("state"); setOtpsSortDir("asc"); }
                        }}
                        title="Sort by Status"
                      >
                        <span>Status</span>
                        <SortArrow
                          colKey="state"
                          activeKey={otpsSortKey}
                          activeDir={otpsSortDir}
                          onChange={(k, d) => {
                            setOtpsSortKey(k);
                            setOtpsSortDir(d);
                          }}
                        />
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[25%]">
                      <button
                        type="button"
                        className="flex items-center gap-2 group bg-transparent border-none p-0 m-0 text-inherit hover:underline focus:outline-none"
                        style={{ width: "100%" }}
                        onClick={() => {
                          if (otpsSortKey === "create_date") setOtpsSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                          else { setOtpsSortKey("create_date"); setOtpsSortDir("asc"); }
                        }}
                        title="Sort by Created"
                      >
                        <span>Created</span>
                        <SortArrow
                          colKey="create_date"
                          activeKey={otpsSortKey}
                          activeDir={otpsSortDir}
                          onChange={(k, d) => {
                            setOtpsSortKey(k);
                            setOtpsSortDir(d);
                          }}
                        />
                      </button>
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[5%]"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {isLoading && (
                    <tr>
                      <td className="px-4 py-2 text-sm" colSpan={4}>
                        Loading...
                      </td>
                    </tr>
                  )}
                  {error && !isLoading && (
                    <tr>
                      <td
                        className="px-4 py-2 text-sm text-red-600"
                        colSpan={4}
                      >
                        {error}
                      </td>
                    </tr>
                  )}
                  {!isLoading && !error && otps && otps.length === 0 && (
                    <tr>
                      <td className="px-4 py-2 text-sm" colSpan={4}>
                        No records
                      </td>
                    </tr>
                  )}
                  {!isLoading &&
                    !error &&
                    otps &&
                    sortedOtps.map((row, index) => (
                      <tr
                        key={row.otp_id}
                        className={`text-left text-base align-top hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }`}
                      >
                        <td className="px-4 py-2.5 whitespace-normal break-words">
                          <div className="flex flex-col">
                            <span className="font-medium">{row.name}</span>
                            <span className="text-sm text-textGray">
                              {row.email}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          {(() => {
                            const otpState = String(
                              row.display_state || row.state || ""
                            ).toLowerCase();
                            const stateLabel =
                              otpState === "used"
                                ? "Used"
                                : otpState === "expired"
                                  ? "Expired"
                                  : "Pending";
                            const stateClass =
                              otpState === "used"
                                ? "text-green-600"
                                : otpState === "expired"
                                  ? "text-red-600"
                                  : "text-amber-600";
                            const subLabel =
                              otpState === "used" && row.used_date
                                ? (() => {
                                    const usedDate = new Date(row.used_date);
                                    if (Number.isNaN(usedDate.getTime())) return "";
                                    const formattedUsedDate = formatAppDateTime(row.used_date)
                                      .replace(",", "")
                                      .replace("AM", "am")
                                      .replace("PM", "pm");
                                    return `Used on ${formattedUsedDate}`;
                                  })()
                                : otpState === "pending" && row.expiry_date
                                  ? `Expires ${formatAppDateTime(row.expiry_date)}`
                                  : "";

                            return (
                              <div className="flex flex-col">
                                <span className={`font-medium ${stateClass}`}>
                                  {stateLabel}
                                </span>
                                {subLabel ? (
                                  <span className="text-xs text-muted-foreground">
                                    {subLabel}
                                  </span>
                                ) : null}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm">
                          {row.create_date
                            ? formatAppDateTime(row.create_date)
                            : ""}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <ActionDropdown
                            actions={[
                              {
                                label: "Delete",
                                className: "text-red-600",
                                onClick: () => handleDeleteOtp(row.otp_id),
                              },
                            ]}
                            alignment="right"
                          />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination controller */}
          {[
            "ACCOUNTS",
            "ROLES",
            "SESSIONS",
            "SIGN INS",
            "IDENTIFICATIONS",
            "CLIENT DEVICES",
            "ONE TIME PASSWORDS",
          ].includes(selectedTab) && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-auto text-sm text-muted-foreground pt-4">
                <span className="text-xs">
                  Showing{" "}
                  {selectedTab === "ACCOUNTS"
                    ? accounts.length
                    : selectedTab === "ROLES"
                      ? roles.length
                      : selectedTab === "SESSIONS"
                        ? sessions.length
                        : selectedTab === "SIGN INS"
                          ? signins.length
                          : selectedTab === "IDENTIFICATIONS"
                            ? identifications.length
                            : selectedTab === "CLIENT DEVICES"
                              ? trustedDevices.length
                              : selectedTab === "ONE TIME PASSWORDS"
                                ? otps.length
                                : 0}{" "}
                  of{" "}
                  {selectedTab === "ACCOUNTS"
                    ? accountsTotal
                    : selectedTab === "ROLES"
                      ? rolesTotal
                      : selectedTab === "SESSIONS"
                        ? sessionsTotal
                        : selectedTab === "SIGN INS"
                          ? signinsTotal
                          : selectedTab === "IDENTIFICATIONS"
                            ? identificationsTotal
                            : selectedTab === "CLIENT DEVICES"
                              ? trustedDevicesTotal
                              : selectedTab === "ONE TIME PASSWORDS"
                                ? otpsTotal
                                : 0}
                </span>
                <div className="flex items-center gap-1 sm:gap-2 justify-between sm:justify-end w-full sm:w-auto">
                  <button
                    className="px-2.5 py-1.5 border border-[#DC1F0033] text-buttonWhite rounded-md hover:bg-gray-100 disabled:opacity-50"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Previous
                  </button>

                  <div className="hidden md:flex items-center gap-1 sm:gap-2">
                    {getPageItems(currentPage, totalPages).map((page, idx) =>
                      page === '...' ? (
                        <span key={"dots-" + idx} className="px-2.5 py-1.5">...</span>
                      ) : (
                        <button
                          key={"page-" + page}
                          className={`px-2.5 py-1.5 border border-[#DC1F0033] rounded-md ${currentPage === page ? "bg-[#DC1F00] text-white" : "hover:bg-gray-100"
                            }`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>

                  <span className="md:hidden px-2 text-xs">
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    className="px-2.5 py-1.5 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-md disabled:opacity-50"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showInvites && (
        <InvitesModal
          onClose={() => setShowInvites(false)}
          onActionComplete={async () => {
            try {
              const q = encodeURIComponent(searchTerm || "");
              const data = await fetchJSON(
                `/api/admin/accounts?page=${currentPage}&limit=${accountsPageSize}&search=${q}&sort=${encodeURIComponent(
                  sortKey
                )}&dir=${encodeURIComponent(
                  sortDir
                )}&filter=${encodeURIComponent(filterBy)}`
              );
              setAccounts(data.data || []);
              setAccountsTotal(data.total || 0);
            } catch (_) { }
          }}
        />
      )}

      {showCreate && (
        <CreateAccountModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            setRefreshTick((t) => t + 1);
            try {
              const q = encodeURIComponent(searchTerm || "");
              const data = await fetchJSON(
                `/api/admin/accounts?page=${currentPage}&limit=${accountsPageSize}&search=${q}&sort=${encodeURIComponent(
                  sortKey
                )}&dir=${encodeURIComponent(
                  sortDir
                )}&filter=${encodeURIComponent(filterBy)}`
              );
              setAccounts(data.data || []);
              setAccountsTotal(data.total || 0);
            } catch (_) { }
          }}
        />
      )}

      {showCreateRole && (
        <CreateRoleModal
          onClose={() => setShowCreateRole(false)}
          onCreated={async () => {
            try {
              const q = encodeURIComponent(searchTerm || "");
              const offset = (currentPage - 1) * otherTabsPageSize;
              const data = await fetchJSON(
                `/api/acl/roles?offset=${offset}&limit=${otherTabsPageSize}&order=${encodeURIComponent(
                  `${rolesSortKey},${rolesSortDir}`
                )}&permissions=true&environmentId=null&search=${q}`
              );
              const normalized = normalizeAclRolesResponse(data);
              setRoles(normalized.rows || []);
              setRolesTotal(normalized.total || 0);
            } catch (_) { }
          }}
        />
      )}

      {editRole && (
        <CreateRoleModal
          role={editRole}
          onClose={() => setEditRole(null)}
          onCreated={async () => {
            try {
              const q = encodeURIComponent(searchTerm || "");
              const offset = (currentPage - 1) * otherTabsPageSize;
              const data = await fetchJSON(
                `/api/acl/roles?offset=${offset}&limit=${otherTabsPageSize}&order=${encodeURIComponent(
                  `${rolesSortKey},${rolesSortDir}`
                )}&permissions=true&environmentId=null&search=${q}`
              );
              const normalized = normalizeAclRolesResponse(data);
              setRoles(normalized.rows || []);
              setRolesTotal(normalized.total || 0);
            } catch (_) {}
          }}
        />
      )}

      <VerifyAccountModal
        isOpen={showVerifyOtpCreate}
        onClose={() => setShowVerifyOtpCreate(false)}
        accountId={
          localStorage.getItem("accountId") ||
          sessionStorage.getItem("accountId") ||
          undefined
        }
        firstName={
          localStorage.getItem("first_name") ||
          localStorage.getItem("firstName") ||
          ""
        }
        lastName={
          localStorage.getItem("last_name") ||
          localStorage.getItem("lastName") ||
          ""
        }
        email={localStorage.getItem("email") || ""}
        onVerified={() => {
          setShowVerifyOtpCreate(false);
          setShowCreateOtpModal(true);
        }}
      />

      <CreateOneTimePasswordModal
        isOpen={showCreateOtpModal}
        onClose={() => setShowCreateOtpModal(false)}
        onCreated={(password) => {
          setShowCreateOtpModal(false);
          setGeneratedOneTimePassword(password || "");
          setRefreshTick((t) => t + 1);
        }}
      />

      <GeneratedPasswordModal
        isOpen={!!generatedOneTimePassword}
        password={generatedOneTimePassword}
        onClose={() => setGeneratedOneTimePassword("")}
      />

      {impersonateDialog.open && (
        <div className="fixed inset-0 bg-black/40 z-[80] flex items-center justify-center px-4">
          <div className="bg-white rounded-md shadow-xl w-full max-w-sm p-4">
            <h3 className="text-[13px] font-semibold text-gray-800 mb-3">Impersonate</h3>

            {impersonationCopyUrl ? (
              <>
                <p className="text-[12px] text-gray-700 mb-2">
                  Warning: opening this URL in your current browser may sign out your current account.
                </p>
                <p className="text-[12px] text-gray-700 mb-3">
                  To avoid this, open an incognito/private window, then copy and paste this URL.
                </p>

                <textarea
                  readOnly
                  value={impersonationCopyUrl}
                  className="w-full h-20 p-2 text-[12px] border border-gray-300 rounded-md bg-gray-50 text-gray-800 resize-none"
                  onFocus={(e) => e.target.select()}
                />

                <div className="flex items-center gap-5 mt-3">
                  <button
                    type="button"
                    className="text-[#DC1F00] text-[13px] font-semibold tracking-wide"
                    onClick={copyImpersonationUrl}
                  >
                    {impersonationCopied ? "COPIED" : "COPY URL"}
                  </button>
                  <button
                    type="button"
                    className="text-gray-800 text-[13px] font-semibold tracking-wide"
                    onClick={closeImpersonateDialog}
                    disabled={impersonateBusy}
                  >
                    CLOSE
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2 mb-4">
                  <label className="flex items-center gap-2 text-[13px] text-gray-800 cursor-pointer">
                    <input
                      type="radio"
                      name="impersonate-mode"
                      checked={impersonateMode === "new"}
                      onChange={() => setImpersonateMode("new")}
                      className="h-3.5 w-3.5 accent-[#DC1F00]"
                    />
                    New window
                  </label>
                  <label className="flex items-center gap-2 text-[13px] text-gray-800 cursor-pointer">
                    <input
                      type="radio"
                      name="impersonate-mode"
                      checked={impersonateMode === "same"}
                      onChange={() => setImpersonateMode("same")}
                      className="h-3.5 w-3.5 accent-[#DC1F00]"
                    />
                    Same window
                  </label>
                </div>

                <div className="flex items-center gap-5">
                  <button
                    type="button"
                    className="text-[#DC1F00] text-[13px] font-semibold tracking-wide disabled:opacity-50"
                    onClick={confirmImpersonation}
                    disabled={impersonateBusy}
                  >
                    {impersonateBusy ? "PLEASE WAIT..." : "CONTINUE"}
                  </button>
                  <button
                    type="button"
                    className="text-gray-800 text-[13px] font-semibold tracking-wide"
                    onClick={closeImpersonateDialog}
                    disabled={impersonateBusy}
                  >
                    CANCEL
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      </fieldset>

      {/* Scanned Image Preview Modal */}
      {previewImageUrl && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 cursor-zoom-out"
          onClick={() => setPreviewImageUrl(null)}
        >
          <div 
            className="relative max-w-4xl max-h-[90vh] bg-white rounded-xl shadow-2xl overflow-hidden p-2 border border-gray-100 flex flex-col items-center cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              type="button" 
              onClick={() => setPreviewImageUrl(null)}
              className="absolute top-3 right-3 z-10 bg-black/50 text-white rounded-full p-2 hover:bg-black/80 transition-colors shadow"
              title="Close Preview"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img 
              src={previewImageUrl} 
              alt="Scan Large Preview" 
              className="max-w-full max-h-[80vh] object-contain rounded-lg" 
            />
            <div className="mt-3 px-4 pb-2 text-sm font-semibold text-gray-700 select-none">
              {previewImageCaption}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

function CreateRoleModal({ onClose, onCreated, role = null }) {
  const { isSuperAdmin } = useAppPermissions();
  const isEdit = !!role?.role_id;
  const [saving, setSaving] = useState(false);
  const [openSectionMenu, setOpenSectionMenu] = useState(null);
  const [form, setForm] = useState(
    role ? roleFormFromAclRole(role) : JSON.parse(JSON.stringify(emptyRoleForm))
  );

  useEffect(() => {
    setForm(
      role ? roleFormFromAclRole(role) : JSON.parse(JSON.stringify(emptyRoleForm))
    );
  }, [role]);

  const submit = async (e) => {
    e.preventDefault();
    if (!isSuperAdmin) {
      showError("Only super admin can create/make changes");
      return;
    }
    if (!form.name) return showError("Name is required");
    try {
      setSaving(true);
      const aclPermissions = buildAclPermissionsFromRoleForm(form);
      const token = localStorage.getItem("token");
      const url = isEdit
        ? `${BASE_URL}/api/acl/roles/${role.role_id}`
        : `${BASE_URL}/api/acl/roles`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: form.name,
          description: form.description,
          level: form.level,
          allPermissions: !!form.enabled,
          aclPermissions,
        }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok)
        throw new Error(j.message || `Failed to ${isEdit ? "update" : "create"} role`);
      showSuccess(`Role ${isEdit ? "updated" : "created"} successfully`);
      if (onCreated) onCreated();
      onClose();
    } catch (e) {
      showError(e.message || `Failed to ${isEdit ? "update" : "create"} role`);
    } finally {
      setSaving(false);
    }
  };

  const setPermissionAccess = (path, value) => {
    setForm((prev) => {
      const next = { ...prev, permissions: { ...prev.permissions } };
      if (path === "accounting" || path === "admin" || path === "system") {
        next.permissions[path] = { ...next.permissions[path], access: value };
      } else if (path === "personnel.officeStaff" || path === "personnel.tempStaff") {
        next.permissions.personnel = { ...next.permissions.personnel };
        const key = path.endsWith("officeStaff") ? "officeStaff" : "tempStaff";
        next.permissions.personnel[key] = value;
      } else if (path === "jobs.shiftActuals" || path === "jobs.shifts") {
        next.permissions.jobs = { ...next.permissions.jobs };
        const key = path.endsWith("shiftActuals") ? "shiftActuals" : "shifts";
        next.permissions.jobs[key] = value;
      } else if (path === "clients.write" || path === "preferences.write") {
        const key = path.startsWith("clients") ? "clients" : "preferences";
        next.permissions[key] = {
          ...next.permissions[key],
          access: value,
          write: value === "write" || value === "full",
        };
      } else if (
        path === "clientPortal.timesheetSignoff" ||
        path === "clientPortal.timesheetSignoffManager"
      ) {
        next.permissions.clientPortal = { ...next.permissions.clientPortal };
        const key = path.endsWith("timesheetSignoffManager")
          ? "timesheetSignoffManager"
          : "timesheetSignoff";
        next.permissions.clientPortal[key] = value;
      }
      return next;
    });
  };

  const getAccessOptions = (path) => {
    switch (path) {
      case "accounting":
      case "clients.write":
      case "personnel.officeStaff":
      case "personnel.tempStaff":
      case "jobs.shiftActuals":
      case "jobs.shifts":
        return ["none", "read", "write"];
      case "admin":
      case "system":
        return ["none", "full"];
      case "preferences.write":
        return ["none", "write"];
      default:
        return form.level === "client"
          ? ["none", "write"]
          : ["none", "read", "write", "full"];
    }
  };

  const getSectionOptions = (section) => {
    const rows = rolePermissionRows.filter((r) => r.section === section);
    const opts = new Set();
    rows.forEach((r) => getAccessOptions(r.path).forEach((v) => opts.add(v)));
    const order = ["none", "read", "write", "full"];
    return order.filter((v) => opts.has(v));
  };

  const setSectionPermissions = (section, value) => {
    setForm((prev) => {
      const next = {
        ...prev,
        permissions: {
          ...prev.permissions,
          accounting: { ...prev.permissions.accounting },
          admin: { ...prev.permissions.admin },
          system: { ...prev.permissions.system },
          clients: { ...prev.permissions.clients },
          preferences: { ...prev.permissions.preferences },
          personnel: { ...prev.permissions.personnel },
          jobs: { ...prev.permissions.jobs },
          clientPortal: { ...prev.permissions.clientPortal },
        },
      };

      rolePermissionRows
        .filter((row) => row.section === section)
        .forEach((row) => {
          const allowed = getAccessOptions(row.path);
          const normalized = allowed.includes(value) ? value : "none";

          if (row.path === "accounting" || row.path === "admin" || row.path === "system") {
            next.permissions[row.path] = { ...next.permissions[row.path], access: normalized };
          } else if (row.path === "personnel.officeStaff" || row.path === "personnel.tempStaff") {
            const key = row.path.endsWith("officeStaff") ? "officeStaff" : "tempStaff";
            next.permissions.personnel[key] = normalized;
          } else if (row.path === "jobs.shiftActuals" || row.path === "jobs.shifts") {
            const key = row.path.endsWith("shiftActuals") ? "shiftActuals" : "shifts";
            next.permissions.jobs[key] = normalized;
          } else if (row.path === "clients.write" || row.path === "preferences.write") {
            const key = row.path.startsWith("clients") ? "clients" : "preferences";
            next.permissions[key] = {
              ...next.permissions[key],
              access: normalized,
              write: normalized === "write" || normalized === "full",
            };
          } else if (
            row.path === "clientPortal.timesheetSignoff" ||
            row.path === "clientPortal.timesheetSignoffManager"
          ) {
            const key = row.path.endsWith("timesheetSignoffManager")
              ? "timesheetSignoffManager"
              : "timesheetSignoff";
            next.permissions.clientPortal[key] = normalized;
          }
        });

      return next;
    });
    setOpenSectionMenu(null);
  };

  const appPermissionRows = [
    {
      section: "Management",
      label: "Accounting",
      description: "Access to Accounting section.",
      path: "accounting",
      value: form.permissions.accounting.access,
    },
    {
      section: "General",
      label: "Admin",
      description:
        'Access the "Admin" section: Includes Accounts, Settings, Messages etc.',
      path: "admin",
      value: form.permissions.admin.access,
    },
    {
      section: "General",
      label: "System",
      description: "Access the System section.",
      path: "system",
      value: form.permissions.system.access,
    },
    {
      section: "Clients",
      label: "Clients",
      description: "",
      path: "clients.write",
      value: form.permissions.clients.access || "none",
    },
    {
      section: "Clients",
      label: "Preferences",
      description:
        "All users with Clients permission can add a new client staff preference, but only those with Write Preferences can edit or remove them.",
      path: "preferences.write",
      value: form.permissions.preferences.access || "none",
    },
    {
      section: "Personnel",
      label: "Office Staff",
      description: "",
      path: "personnel.officeStaff",
      value: form.permissions.personnel.officeStaff,
    },
    {
      section: "Personnel",
      label: "Temp Staff",
      description: "",
      path: "personnel.tempStaff",
      value: form.permissions.personnel.tempStaff,
    },
    {
      section: "Jobs",
      label: "Shift Actuals",
      description: "",
      path: "jobs.shiftActuals",
      value: form.permissions.jobs.shiftActuals,
    },
    {
      section: "Jobs",
      label: "Shifts",
      description: "",
      path: "jobs.shifts",
      value: form.permissions.jobs.shifts,
    },
  ];
  const clientPermissionRows = [
    {
      section: "Clients",
      label: "Timesheet Signoff",
      description: "Access to sign off on timesheets.",
      path: "clientPortal.timesheetSignoff",
      value: form.permissions.clientPortal.timesheetSignoff,
    },
    {
      section: "General",
      label: "Timesheet Signoff Manager",
      description: "",
      path: "clientPortal.timesheetSignoffManager",
      value: form.permissions.clientPortal.timesheetSignoffManager,
    },
  ];
  const rolePermissionRows =
    form.level === "client" ? clientPermissionRows : appPermissionRows;

  const setAllPermissions = (enabled, level = form.level) => {
    const appDefault = enabled ? "write" : "none";
    const generalDefault = enabled ? "full" : "none";
    const clientDefault = enabled ? "write" : "none";
    setForm((prev) => ({
      ...prev,
      enabled,
      permissions: {
        ...prev.permissions,
        accounting: { ...prev.permissions.accounting, access: appDefault },
        admin: { ...prev.permissions.admin, access: generalDefault },
        system: { ...prev.permissions.system, access: generalDefault },
        clients: {
          ...prev.permissions.clients,
          access: appDefault,
          write: appDefault === "write" || appDefault === "full",
        },
        preferences: {
          ...prev.permissions.preferences,
          access: appDefault === "none" ? "none" : "write",
          write: appDefault !== "none",
        },
        personnel: {
          ...prev.permissions.personnel,
          officeStaff: appDefault,
          tempStaff: appDefault,
        },
        jobs: {
          ...prev.permissions.jobs,
          shiftActuals: appDefault,
          shifts: appDefault,
        },
        clientPortal: {
          ...prev.permissions.clientPortal,
          timesheetSignoff: clientDefault,
          timesheetSignoffManager: clientDefault,
        },
      },
      level,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-3">
      <div className="bg-card text-foreground rounded-lg shadow w-full max-w-2xl max-h-[90vh] overflow-hidden border border-border">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-lg font-semibold">
            {isEdit ? "Edit Role" : "Create Role"}
          </h3>
          <button
            className="text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            ✖
          </button>
        </div>
        <form onSubmit={submit} className="p-4 overflow-y-auto max-h-[65vh]">
          <div className="space-y-6">
            <div className="rounded-lg border border-border p-4 space-y-3">
              <h4 className="text-sm font-semibold tracking-wide text-muted-foreground">
                Role Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Name*</label>
                  <input
                    type="text"
                    className="w-full border border-border rounded-lg px-3 py-2 bg-white text-foreground"
                    value={form.name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <input
                    type="text"
                    className="w-full border border-border rounded-lg px-3 py-2 bg-white text-foreground"
                    value={form.description}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Level*</label>
                  <select
                    className="w-full border border-border rounded-lg px-3 py-2 bg-white text-foreground"
                    value={form.level}
                    onChange={(e) => {
                      const nextLevel = e.target.value;
                      setAllPermissions(!!form.enabled, nextLevel);
                    }}
                    required
                  >
                    <option value="app">App</option>
                    <option value="client">Client</option>
                  </select>
                </div>
                <div className="flex items-end pb-2">
                  <label className="inline-flex items-center gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      className="accent-[#DC1F00]"
                      checked={!!form.enabled}
                      onChange={(e) => setAllPermissions(e.target.checked)}
                    />
                    All Permissions
                  </label>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-border overflow-hidden">
              <div className="px-4 py-3 bg-secondary/40 border-b border-border">
                <h4 className="text-sm font-semibold tracking-wide text-muted-foreground">
                  Permissions Access
                </h4>
              </div>
              <div className="divide-y divide-border">
                {rolePermissionRows.map((row, index) => (
                  <div key={row.path}>
                    {(index === 0 ||
                      row.section !== rolePermissionRows[index - 1].section) && (
                      <div className="px-4 py-2 bg-[#DC1F000D] text-sm font-medium text-gray-600 flex items-center justify-between relative">
                        <span>{row.section}</span>
                        <button
                          type="button"
                          className="text-gray-600 hover:text-gray-800 px-2 text-xl font-bold leading-none"
                          disabled={!!form.enabled}
                          onClick={() =>
                            setOpenSectionMenu((prev) =>
                              prev === row.section ? null : row.section
                            )
                          }
                          aria-label={`Bulk set ${row.section} permissions`}
                        >
                          ⋮
                        </button>
                        {openSectionMenu === row.section && (
                          <div className="absolute right-2 top-8 bg-white border border-gray-200 rounded shadow-md z-30 min-w-[150px]">
                            {getSectionOptions(row.section).map((v) => (
                              <button
                                key={`${row.section}-${v}`}
                                type="button"
                                className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                onClick={() => setSectionPermissions(row.section, v)}
                              >
                                Set all to{" "}
                                {v === "none"
                                  ? "None"
                                  : v === "read"
                                    ? "Read"
                                    : v === "write"
                                      ? "Write"
                                      : "Full"}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-[1fr_220px] gap-2 items-center">
                      <div>
                        <div className="text-sm font-medium">{row.label}</div>
                        {row.description ? (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {row.description}
                          </div>
                        ) : null}
                      </div>
                      <div>
                        <select
                          className="w-full border border-border rounded-lg px-3 py-2 bg-white text-foreground"
                          disabled={!!form.enabled}
                          value={
                            getAccessOptions(row.path).includes(row.value || "none")
                              ? (row.value || "none")
                              : "none"
                          }
                          onChange={(e) => setPermissionAccess(row.path, e.target.value)}
                        >
                          {getAccessOptions(row.path).map((v) => (
                            <option key={v} value={v}>
                              {v === "none"
                                ? "None"
                                : v === "read"
                                  ? "Read"
                                  : v === "write"
                                    ? "Write"
                                    : "Full"}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              disabled={saving}
              type="submit"
              className="px-4 py-2 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-lg disabled:opacity-50"
            >
              {saving
                ? isEdit
                  ? "Saving..."
                  : "Creating..."
                : isEdit
                  ? "Save Role"
                  : "Create Role"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Accounts;

function InvitesModal({ onClose, onActionComplete }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, email }
  const [sortKey, setSortKey] = useState("create_date");
  const [sortDir, setSortDir] = useState("desc");
  const [invitesStatus, setInvitesStatus] = useState("all");
  const [showDeleted, setShowDeleted] = useState(false);
  const [resultsLimit, setResultsLimit] = useState(25);
  const invitesFilterPanel = useFilterPanel();

  const loadInvites = async () => {
    try {
      setLoading(true);
      setErr("");
      const fetchAll = resultsLimit === "all";
      const effectiveLimit = fetchAll ? 200 : Number(resultsLimit) || 25;
      const effectivePage = fetchAll ? 1 : page;
      const params = new URLSearchParams({
        page: String(effectivePage),
        limit: String(effectiveLimit),
        search: query,
        status: invitesStatus || "all",
        sort: sortKey,
        dir: sortDir,
        showDeleted: showDeleted ? "1" : "0",
      });

      const res = await fetch(`${BASE_URL}/api/admin/account-invites?${params.toString()}`, {
        credentials: "include",
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.message || "Failed to load");

      let allRows = j.data || [];
      if (fetchAll && (j.totalPages || 1) > 1) {
        for (let p = 2; p <= (j.totalPages || 1); p += 1) {
          const nextParams = new URLSearchParams({
            page: String(p),
            limit: String(effectiveLimit),
            search: query,
            status: invitesStatus || "all",
            sort: sortKey,
            dir: sortDir,
            showDeleted: showDeleted ? "1" : "0",
          });
          const nextRes = await fetch(
            `${BASE_URL}/api/admin/account-invites?${nextParams.toString()}`,
            { credentials: "include" }
          );
          const nextJson = await nextRes.json().catch(() => ({}));
          if (!nextRes.ok) {
            throw new Error(nextJson.message || "Failed to load all invites");
          }
          allRows = allRows.concat(nextJson.data || []);
        }
      }

      setInvites(allRows);
      setTotal(j.total || allRows.length || 0);
      setTotalPages(fetchAll ? 1 : Math.max(1, j.totalPages || 1));
    } catch (e) {
      setErr(e.message || "Failed to load invites");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvites();
  }, [page, invitesStatus, sortKey, sortDir, showDeleted, resultsLimit]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) loadInvites();
      else setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1);
  };

  const SortHeader = ({ label, sortItem, className = "" }) => (
    <th
      className={`py-2 px-2 border border-border cursor-pointer hover:bg-[#dc1f00] transition-colors ${className}`}
      onClick={() => handleSort(sortItem)}
    >
      <div className="flex items-center justify-between">
        <span>{label}</span>
        <span className="text-[10px] ml-1 opacity-80">
          {sortKey === sortItem ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </div>
    </th>
  );

  const resendInvite = async (id) => {
    try {
      setBusyId(id);
      const res = await fetch(
        `${BASE_URL}/api/admin/account-invites/${id}/resend`,
        { method: "POST", credentials: "include" }
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.message || "Failed to resend");
      showSuccess("Invite resent");
      await loadInvites();
      if (onActionComplete) onActionComplete();
    } catch (e) {
      showError(e.message || "Server error");
    } finally {
      setBusyId(null);
    }
  };

  const deleteInvite = async (id) => {
    try {
      setBusyId(id);
      setDeleteConfirm(null);
      const res = await fetch(`${BASE_URL}/api/admin/account-invites/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.message || "Failed to delete invite");
      showSuccess("Invite deleted successfully");
      await loadInvites();
      if (onActionComplete) onActionComplete();
    } catch (e) {
      showError(e.message || "Failed to delete invite");
    } finally {
      setBusyId(null);
    }
  };

  const stateClass = (state) => {
    if (state === "deleted") return "text-red-600";
    if (state === "expired") return "text-orange-600";
    if (state === "pending") return "text-yellow-600";
    if (state === "used") return "text-green-600";
    return "text-muted-foreground";
  };

  const chips = buildAdminInvitesFilterChips({ invitesStatus, sortDir });

  const numericLimit = resultsLimit === "all" ? 200 : Number(resultsLimit) || 25;
  const showingFrom = total > 0 ? (resultsLimit === "all" ? 1 : (page - 1) * numericLimit + 1) : 0;
  const showingTo = total > 0 ? (resultsLimit === "all" ? total : Math.min(page * numericLimit, total)) : 0;

  return createPortal(
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-3">
      <div className="bg-card text-foreground rounded-lg shadow w-full max-w-5xl max-h-[85vh] overflow-hidden border border-border flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-lg font-semibold">Invites</h3>
          <button
            className="text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            ✖
          </button>
        </div>

        <div className="p-3 sm:p-4 border-b border-border">
          <AtlasListToolbar
            searchValue={query}
            onSearchChange={(value) => {
              setQuery(value);
              setPage(1);
            }}
            searchPlaceholder="Search email or inviter"
            onFilterClick={invitesFilterPanel.toggle}
            filterOpen={invitesFilterPanel.isOpen}
            onRefresh={loadInvites}
            refreshSpinning={loading}
            resultsMeta={{
              startRow: showingFrom,
              endRow: showingTo,
              totalCount: total,
              pageSize: resultsLimit === "all" ? 200 : Number(resultsLimit),
              pageSizeOptions: [12, 25, 50, 100, 200],
              onPageSizeChange: (size) => {
                setResultsLimit(size);
                setPage(1);
              },
              sortBy: sortKey,
              sortOrder: sortDir,
              sortOptions: INVITES_SORT_OPTIONS,
              onSortByChange: (value) => {
                setSortKey(value);
                setPage(1);
              },
              onSortOrderToggle: () => {
                setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
                setPage(1);
              },
            }}
            chipBar={
              chips.length > 0 ? (
                <AtlasFilterChipBar
                  chips={chips}
                  onChipClick={(chip) => {
                    if (chip.fieldId) invitesFilterPanel.focusField(chip.fieldId);
                    else invitesFilterPanel.open();
                  }}
                  onRemove={(chip) => {
                    if (chip.key === "invitesStatus") setInvitesStatus("all");
                    setPage(1);
                  }}
                  onClearAll={() => {
                    setInvitesStatus("all");
                    setPage(1);
                  }}
                />
              ) : null
            }
          />
        </div>

        <div
          className="pt-2 px-3 pb-6 sm:pt-2 sm:px-4 sm:pb-6 overflow-auto flex-1"
          style={{ maxHeight: "calc(85vh - 96px)" }}
        >
          {loading && <div className="text-sm">Loading…</div>}
          {err && !loading && <div className="text-sm text-red-600">{err}</div>}
          {!loading && !err && (
            <div className="relative overflow-x-auto overflow-y-auto max-h-[60vh]">
              <table className="w-full table-auto border-collapse text-sm">
                <thead className="bg-[#dc1f0090] text-white z-20">
                  <tr>
                    <SortHeader label="Email" sortItem="email" className="w-[32%]" />
                    <SortHeader label="Type" sortItem="type" className="w-[12%]" />
                    <SortHeader label="Invitation Creator" sortItem="invitation_creator" className="w-[22%]" />
                    <SortHeader label="Status" sortItem="status" className="w-[14%]" />
                    <SortHeader label="Created" sortItem="created" className="w-[14%] whitespace-nowrap" />
                    <th className="py-2 px-2 border border-border w-[6%]">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {invites.length === 0 && (
                    <tr>
                      <td className="py-3 px-2 text-sm text-center" colSpan={6}>
                        No invites found
                      </td>
                    </tr>
                  )}
                  {invites.map((r) => (
                    <tr
                      key={r.account_request_id}
                      className="border-t border-border align-top"
                    >
                      <td className="py-2 px-2 whitespace-normal break-words max-w-[260px] md:max-w-none">
                        {r.email}
                      </td>
                      <td className="py-2 px-2 capitalize">
                        {r.type || "—"}
                      </td>
                      <td className="py-2 px-2 whitespace-normal break-words max-w-[220px] md:max-w-none">
                        {r.invited_by || "—"}
                      </td>
                      <td className="py-2 px-2">
                        <span
                          className={`capitalize ${stateClass(
                            r.display_state || r.state
                          )} border border-border rounded px-2 py-0.5`}
                        >
                          {r.display_state || r.state || "pending"}
                        </span>
                      </td>
                      <td className="py-2 px-2 whitespace-nowrap">
                        {r.create_date
                          ? formatAppDate(r.create_date)
                          : "—"}
                      </td>
                      <td className="py-2 px-2">
                        {!showDeleted && (r.display_state || r.state) !== "deleted" ? (
                          <ActionDropdown
                            actions={[
                              {
                                label: busyId === r.account_request_id ? "Resending..." : "Resend Invite",
                                onClick: () => resendInvite(r.account_request_id),
                              },
                              {
                                label: "Delete Invite",
                                danger: true,
                                onClick: () =>
                                  setDeleteConfirm({
                                    id: r.account_request_id,
                                    email: r.email,
                                    state: r.display_state || r.state,
                                  }),
                              },
                            ]}
                          />
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Delete Invite
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete the invite for{" "}
                <strong className="text-gray-900">{deleteConfirm.email}</strong>?
                <br />
                {deleteConfirm.state === "used" && (
                  <span className="block mt-2 text-red-700 font-semibold bg-red-50 border border-red-200 rounded px-2 py-1">
                    ⚠️ Warning: This invite has been used and the account is active.
                    Deleting will only remove the invite record, not the account.
                  </span>
                )}
                <span className="block mt-2 text-red-600 font-medium">
                  This action cannot be undone.
                </span>
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
                  disabled={busyId === deleteConfirm.id}
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteInvite(deleteConfirm.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium disabled:opacity-50"
                  disabled={busyId === deleteConfirm.id}
                >
                  {busyId === deleteConfirm.id ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        <AtlasFilterPanel
          isOpen={invitesFilterPanel.isOpen}
          onClose={invitesFilterPanel.close}
          onApply={() => {
            setPage(1);
            invitesFilterPanel.close();
          }}
          onClear={() => {
            setInvitesStatus("all");
            setSortKey("create_date");
            setSortDir("desc");
            setShowDeleted(false);
            setPage(1);
            invitesFilterPanel.close();
          }}
        >
          <AdminInvitesFilterFields
            focusedFieldId={invitesFilterPanel.focusedFieldId}
            onFocusClear={invitesFilterPanel.clearFocusedField}
            invitesStatus={invitesStatus}
            setInvitesStatus={setInvitesStatus}
            sortKey={sortKey}
            setSortKey={setSortKey}
            sortDir={sortDir}
            setSortDir={setSortDir}
          />
          <div className="mt-4 flex items-center gap-2">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => {
                const checked = e.target.checked;
                setShowDeleted(checked);
                if (checked) {
                  setInvitesStatus("all");
                }
                setPage(1);
              }}
              className="h-4 w-4 text-sidebar-active border-gray-300 rounded focus:ring-sidebar-active"
            />
            <label className="text-sm font-medium text-gray-700">
              Show Deleted
            </label>
          </div>
        </AtlasFilterPanel>
      </div>
    </div>,
    document.body
  );
}

function CreateAccountModal({ onClose, onCreated }) {
  const auth = useAuth();
  const [tab, setTab] = useState("invite");
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email: "",
    role_ids: [],
    method: "invite",
    activation_method: "auto",
    first_name: "",
    last_name: "",
  });
  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        setLoadingRoles(true);
        const res = await fetch(`${BASE_URL}/api/acl/roles?offset=0&limit=500&order=hierarchy,asc&permissions=true&environmentId=null`, {
          credentials: "include",
        });
        const j = await res.json();
        const normalized = normalizeAclRolesResponse(j);
        const appLevelRoles = (normalized.rows || []).filter((r) => {
          const level = String(r?.level || "").toLowerCase();
          if (level) return level === "app";
          const appAccess = String(r?.app_access ?? r?.app ?? "").toLowerCase();
          return ["1", "true", "yes", "app"].includes(appAccess);
        });
        setRoles(appLevelRoles);
      } catch (_) {
      } finally {
        setLoadingRoles(false);
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return showError("Email is required");
    if (!Array.isArray(form.role_ids) || form.role_ids.length === 0)
      return showError("Select at least one role");
    if (!["invite", "signin"].includes(form.method))
      return showError("Select a create method");
    if (form.method === "signin") {
      if (!form.first_name?.trim()) return showError("First name is required");
      if (!form.last_name?.trim()) return showError("Last name is required");
    }
    try {
      setSaving(true);
      // Prefer token from hook but fall back to localStorage so transient
      // initialization timing doesn't cause the header to be omitted.
      const token = (auth && auth.token) || localStorage.getItem("token");
      // also pick up accountId as a fallback for creator attribution
      const accountId =
        (auth && auth.accountId) || localStorage.getItem("accountId");

      // Prefer token from hook but fall back to localStorage so transient
      // initialization timing doesn't cause the header to be omitted.
      // Debug info to help diagnose missing headers in runtime logs / DevTools
      try {
        console.debug(
          "CreateAccount submit - token present:",
          !!token,
          "token_snip:",
          token ? token.slice(0, 8) : null,
          "accountId:",
          accountId
        );
      } catch (_) { }

      // Build headers object explicitly so we can inspect it in DevTools
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(accountId ? { "X-Author-Account-Id": `${accountId}` } : {}),
      };
      try {
        console.debug("CreateAccount headers:", headers);
      } catch (_) { }

      const res = await fetch(`${BASE_URL}/api/admin/accounts`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
          email: form.email,
          role_ids: form.role_ids,
          send_invite: form.method === "invite",
          first_name: form.first_name,
          last_name: form.last_name,
          name: `${form.first_name || ""} ${form.last_name || ""}`.trim(),
          activation_method: form.activation_method,
        }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.message || "Failed to create");
      showSuccess(form.method === "invite" ? "Invite sent" : "Account created");
      if (onCreated) onCreated();
      onClose();
    } catch (e2) {
      showError(e2.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-3">
      <div className="bg-card text-foreground rounded-lg shadow w-full max-w-2xl max-h-[90vh] overflow-hidden border border-border">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-lg font-semibold">Create</h3>
        </div>
        <form
          onSubmit={submit}
          className="p-4 space-y-4 overflow-y-auto max-h-[65vh]"
        >
          <div>
            <label className="block text-sm font-medium">Create Method*</label>
            <div className="mt-2 flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="accent-[#DC1F00]"
                  checked={form.method === "invite"}
                  onChange={(e) =>
                    setField("method", e.target.checked ? "invite" : "")
                  }
                />
                <span className="font-medium">
                  Create Account via Invite Link
                </span>
              </label>
              <p className="text-xs text-muted-foreground ml-6">
                The person invited via email can create an Account using a
                supplied invite link.
              </p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="accent-[#DC1F00]"
                  checked={form.method === "signin"}
                  onChange={(e) =>
                    setField("method", e.target.checked ? "signin" : "")
                  }
                />
                <span className="font-medium">Create Sign In Account</span>
              </label>
              <p className="text-xs text-muted-foreground ml-6">
                This account uses an email and password to authenticate.
              </p>
            </div>
          </div>

          {form.method === "signin" && (
            <div className="mt-4 flex gap-6">
              <div className="flex flex-col w-full">
                <label className="block text-sm font-medium mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  placeholder="Enter first name"
                  required
                  value={form.first_name}
                  onChange={(e) => setField("first_name", e.target.value)}
                  className="w-full border rounded-lg px-3 py-1 focus:ring-1 focus:ring-red-400"
                />
              </div>
              <div className="flex flex-col w-full">
                <label className="block text-sm font-medium mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  placeholder="Enter last name"
                  required
                  value={form.last_name}
                  onChange={(e) => setField("last_name", e.target.value)}
                  className="w-full border rounded-lg px-3 py-1 focus:ring-1 focus:ring-red-400"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium">Email*</label>
            <input
              type="email"
              required
              className="w-full border border-border rounded-lg px-3 py-2 bg-white text-foreground"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
            />
          </div>

          {form.method === "signin" && (
            <div>
              <label className="block text-sm font-medium">
                Activation Method
              </label>
              <div className="mt-2 flex flex-col gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    className="accent-[#DC1F00]"
                    checked={form.activation_method === "auto"}
                    onChange={(e) =>
                      setField(
                        "activation_method",
                        e.target.checked ? "auto" : ""
                      )
                    }
                  />
                  <span className="font-medium">Auto Activation</span>
                </label>
                <p className="text-xs text-muted-foreground ml-6">
                  The Account is created with status of Active.
                </p>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    className="accent-[#DC1F00]"
                    checked={form.activation_method === "manual"}
                    onChange={(e) =>
                      setField(
                        "activation_method",
                        e.target.checked ? "manual" : ""
                      )
                    }
                  />
                  <span className="font-medium">Manual Activation</span>
                </label>
                <p className="text-xs text-muted-foreground ml-6">
                  This Account is created with status of Pending Activation. An
                  email will be sent to prompt the person to activate their
                  account using a supplied activation link.
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium">Roles*</label>
            <div className="flex flex-wrap gap-3 border border-border rounded-lg p-2 min-h-[42px]">
              {loadingRoles ? (
                <span className="text-sm text-muted-foreground">
                  Loading roles…
                </span>
              ) : (
                roles.map((r) => (
                  <label
                    key={r.role_id}
                    className="flex items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      className="accent-[#DC1F00]"
                      checked={form.role_ids.includes(r.role_id)}
                      onChange={(e) => {
                        setField(
                          "role_ids",
                          e.target.checked
                            ? [...form.role_ids, r.role_id]
                            : form.role_ids.filter((id) => id !== r.role_id)
                        );
                      }}
                    />
                    <span>{r.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              disabled={saving}
              type="submit"
              className="px-4 py-2 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-lg disabled:opacity-50"
            >
              {saving
                ? "Saving…"
                : form.method === "invite"
                  ? "Send Invite"
                  : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


